import type { GetAllFeatureFlagsQuery } from "~/gql/queries/featureFlag.generated";
import type { GetFernieSuggestionsSettingQuery, GetTenantQuery } from "~/gql/queries/tenant.generated";
import type { GetMeQuery } from "~/gql/queries/users.generated";
import type { RootState } from "~/store";

import { matchPath } from "react-router-dom";

import { ApplicationEnum } from "~/@types/enums";
import { MasterOrderStatus, OrderStatus, OrderType } from "~/gql/graphqlBase.generated";
import { type WalkthroughAvailabilityState, type WalkthroughDefinition } from "~/walkthroughs/types";
import { isBpfTenant } from "~fulfill/orders/helpers/invoiceEligibility";
import orderDocumentActions from "~fulfill/orders/helpers/orderDocumentActions";
import { allowsAddingLineItems, isAllocationEditable } from "~fulfill/orders/helpers/orderEditability";
import productionAllocationAccess from "~fulfill/orders/helpers/productionAllocationAccess";

export const TOUR_ANCHORS = {
	scheduleMasterOpen: "fulfill-master-schedule-open",
	scheduleMasterStart: "fulfill-master-schedule-start",
	scheduleMasterEnd: "fulfill-master-schedule-end",
	scheduleMasterGenerate: "fulfill-master-schedule-generate",
	scheduleMasterWeek: "fulfill-master-schedule-week",
	scheduleMasterQuantityEntered: "fulfill-master-schedule-quantity-entered",
	scheduleMasterSave: "fulfill-master-schedule-save",
	scheduleMasterDetails: "fulfill-master-schedule-details",
	scheduleMasterScheduledTab: "fulfill-master-schedule-scheduled-tab",
	scheduleMasterScheduledList: "fulfill-master-schedule-scheduled-list",
	reuseCustomerTypeField: "fulfill-customer-same-as-type",
	reuseCustomerTypeSuggestion: "fulfill-customer-same-as-suggestion",
	reuseCustomerTypeUse: "fulfill-customer-same-as-use",
	reuseCustomerTypeUsed: "fulfill-customer-same-as-used",
	reuseCustomerTypeSave: "fulfill-customer-same-as-save",
	allocateToProductionOpen: "fulfill-orders-production-open",
	allocateToProductionSettings: "fulfill-orders-production-settings",
	allocateToProductionPlanSelected: "fulfill-orders-production-plan-selected",
	allocateToProductionPreview: "fulfill-orders-production-preview",
	allocateToProductionResults: "fulfill-orders-production-results",
	importCustomerMatchesUpload: "fulfill-customers-import-upload",
	importCustomerMatchesColumn: "fulfill-customers-import-fernie-column",
	importCustomerMatchesRibbon: "fulfill-customers-import-fernie-ribbon",
	importCustomerMatchesUsed: "fulfill-customers-import-fernie-used",
	importCustomerMatchesSameAs: "fulfill-customers-import-fernie-same-as",
	importCustomerMatchesConfirm: "fulfill-customers-import-fernie-confirm",
	importCustomerMatchesUndo: "fulfill-customers-import-fernie-undo",
	importCustomerMatchesUndone: "fulfill-customers-import-fernie-undone",
	linkItemFromOrderPicker: "fulfill-order-item-picker",
	linkItemFromOrderProduction: "fulfill-item-production-picker",
	linkItemFromOrderNumber: "fulfill-item-production-number",
	linkItemFromOrderProductionSave: "fulfill-item-production-save",
	linkItemFromOrderRecipe: "fulfill-item-add-recipe",
	adjustHardgoodsAdjust: "produce-hardgoods-adjust",
	adjustHardgoodsList: "produce-hardgoods-list",
	adjustHardgoodsQuantity: "produce-hardgoods-quantity",
	adjustHardgoodsQuantityEntered: "produce-hardgoods-quantity-entered",
	adjustHardgoodsSave: "produce-hardgoods-save",
	adjustInventoryAdjust: "fulfill-inventory-adjust",
	adjustInventoryList: "fulfill-inventory-list",
	adjustInventoryQuantity: "fulfill-inventory-quantity",
	adjustInventoryQuantityEntered: "fulfill-inventory-quantity-entered",
	adjustInventorySave: "fulfill-inventory-save",
	allocateAndPrintPullSlipAllocate: "fulfill-order-allocate",
	allocateAndPrintPullSlipPickSlip: "fulfill-order-pick-slip",
	allocateAndPrintPullSlipPickSlipView: "fulfill-order-pick-slip-view",
	allocateAndPrintPullSlipQuickAllocate: "fulfill-order-quick-allocate",
	takeFromOtherOrdersAdvanced: "fulfill-order-allocate-advanced",
	takeFromOtherOrdersClaimsLink: "fulfill-order-claimed-by-other-orders",
	takeFromOtherOrdersClaimsPanel: "fulfill-order-claiming-orders",
	takeFromOtherOrdersConfirm: "fulfill-order-take-confirm",
	takeFromOtherOrdersDialogOpen: "fulfill-order-allocate-dialog",
	takeFromOtherOrdersLine: "fulfill-order-allocate-line",
	takeFromOtherOrdersRunningTotal: "fulfill-order-take-running-total",
	takeFromOtherOrdersSave: "fulfill-order-take-save",
	takeFromOtherOrdersTakeEntered: "fulfill-order-take-units-entered",
	takeFromOtherOrdersTakeInput: "fulfill-order-take-units",
	buildLoadListGenerate: "restock-loadlist-generate",
	buildLoadListNewSession: "restock-loadlist-new-session",
	buildLoadListSessionManager: "restock-loadlist-session-manager",
	buildLoadListStationSelect: "restock-loadlist-station-select",
	buildLoadListStations: "restock-loadlist-stations",
	buildReportBlankReport: "reports-blank-report",
	buildReportCreate: "reports-blank-create",
	buildReportDataSources: "reports-blank-data-sources",
	buildReportName: "reports-blank-name",
	createCustomerAddCustomer: "fulfill-customers-add-customer",
	createCustomerName: "fulfill-customers-name",
	createCustomerNameEntered: "fulfill-customers-name-entered",
	createCustomerSave: "fulfill-customers-save",
	createInvoiceIssue: "fulfill-order-create-invoice",
	createItemAddItem: "fulfill-items-add-item",
	createItemCode: "fulfill-items-code",
	createItemCodeEntered: "fulfill-items-code-entered",
	createItemName: "fulfill-items-name",
	createItemNameEntered: "fulfill-items-name-entered",
	createItemSave: "fulfill-items-save",
	massUpdateItemsApply: "fulfill-items-mass-update-apply",
	massUpdateItemsChangeReady: "fulfill-items-mass-update-change-ready",
	massUpdateItemsChosen: "fulfill-items-mass-update-chosen",
	massUpdateItemsField: "fulfill-items-mass-update-field",
	massUpdateItemsNext: "fulfill-items-mass-update-next",
	massUpdateItemsOpen: "fulfill-items-mass-update-open",
	massUpdateItemsResults: "fulfill-items-mass-update-results",
	massUpdateItemsReview: "fulfill-items-mass-update-review",
	massUpdateItemsSearch: "fulfill-items-mass-update-search",
	createKitAddKit: "fulfill-kits-add-kit",
	createKitBasicsNext: "fulfill-kits-basics-next",
	createKitContainer: "fulfill-kits-container",
	createKitContainerSelected: "fulfill-kits-container-selected",
	createKitContents: "fulfill-kits-contents",
	createKitContentsNext: "fulfill-kits-contents-next",
	createKitLineAdded: "fulfill-kits-line-added",
	createKitName: "fulfill-kits-name",
	createKitNameEntered: "fulfill-kits-name-entered",
	createKitReview: "fulfill-kits-review",
	createKitSave: "fulfill-kits-save",
	createKitWizardOpen: "fulfill-kits-wizard-create-open",
	createOrderAddOrder: "fulfill-orders-add-order",
	createOrderCustomerPicker: "fulfill-orders-customer-picker",
	createOrderCustomerSelected: "fulfill-orders-customer-selected",
	createOrderDetails: "fulfill-orders-details",
	createOrderNewFormOpen: "fulfill-orders-new-form-open",
	createOrderSave: "fulfill-orders-save",
	createPriceLevelAddLevel: "fulfill-pricing-add-level",
	createPriceLevelPrice: "fulfill-pricing-price",
	createPriceLevelSave: "fulfill-pricing-save",
	createPriceLevelStartDate: "fulfill-pricing-start-date",
	createPriceLevelTarget: "fulfill-pricing-target",
	createProductionJobAddJob: "produce-schedule-add-job",
	createProductionJobRecipe: "produce-schedule-recipe",
	createProductionJobSave: "produce-schedule-save",
	createProductionJobWeek: "produce-schedule-week",
	printShipDayPaperworkDayPicked: "fulfill-deliveries-ship-day-picked",
	printShipDayPaperworkDialogPrint: "fulfill-deliveries-paperwork-print",
	printShipDayPaperworkPrint: "fulfill-deliveries-print-paperwork",
	printShipDayPaperworkSelected: "fulfill-deliveries-selected",
	printShipDayPaperworkSelection: "fulfill-deliveries-selection",
	printShipDayPaperworkShipDay: "fulfill-deliveries-ship-day",
	printShipDayPaperworkViewer: "fulfill-deliveries-paperwork-viewer",
	recordProductionAddLocation: "produce-schedule-record-add-location",
	recordProductionLocationRow: "produce-schedule-record-location-row",
	recordProductionMenuItem: "produce-schedule-record-menu-item",
	recordProductionOverrides: "produce-schedule-record-overrides",
	recordProductionPlaced: "produce-schedule-record-placed",
	recordProductionPlacedReady: "produce-schedule-record-placed-ready",
	recordProductionQuantity: "produce-schedule-record-quantity",
	recordProductionQuantityEntered: "produce-schedule-record-quantity-entered",
	recordProductionSave: "produce-schedule-record-save",
	createPurchaseOrderAddPo: "fulfill-purchasing-add-po",
	createPurchaseOrderLine: "fulfill-purchasing-line",
	createPurchaseOrderLineSelected: "fulfill-purchasing-line-selected",
	createPurchaseOrderNewFormOpen: "fulfill-purchasing-new-form-open",
	createPurchaseOrderSave: "fulfill-purchasing-save",
	createPurchaseOrderVendor: "fulfill-purchasing-vendor",
	createPurchaseOrderVendorSelected: "fulfill-purchasing-vendor-selected",
	createRecipeAddRecipe: "produce-recipes-add-recipe",
	createRecipeCatalog: "produce-recipes-catalog",
	createRecipeCategory: "produce-recipes-category",
	createRecipeDetails: "produce-recipes-details",
	createRecipeSave: "produce-recipes-save",
	createSiteLocationAddLocation: "fulfill-locations-add-location",
	createSiteLocationName: "fulfill-locations-name",
	createSiteLocationNameEntered: "fulfill-locations-name-entered",
	createSiteLocationSave: "fulfill-locations-save",
	createVendorAddVendor: "fulfill-vendors-add-vendor",
	createVendorName: "fulfill-vendors-name",
	createVendorNameEntered: "fulfill-vendors-name-entered",
	createVendorSave: "fulfill-vendors-save",
	editRecipeVariationsEditing: "produce-recipes-grid-editing",
	editRecipeVariationsGrowWeeks: "produce-recipes-grid-grow-weeks",
	editRecipeVariationsSaveAway: "produce-recipes-grid-save-away",
	editRecipeVariationsUpdateJobs: "produce-recipes-grid-update-jobs",
	filterAndExportExport: "fulfill-orders-export",
	globalSearch: "nav-global-search",
	gridSaveLayout: "grid-save-layout",
	gridViewTab: "grid-ribbon-view-tab",
	importMaterialItemsContinue: "produce-material-items-import-continue",
	importMaterialItemsFileChosen: "produce-material-items-import-file-chosen",
	importMaterialItemsStepper: "produce-material-items-import-stepper",
	importMaterialItemsTemplate: "produce-material-items-import-template",
	importMaterialItemsUpload: "produce-material-items-import-upload",
	linkProductionItemList: "produce-production-items-tabs",
	linkProductionItemSalesItem: "produce-production-items-sales-item",
	linkProductionItemSave: "produce-production-items-save",
	orderAdvanceStatus: "fulfill-order-advance-status",
	orderDetailsToggle: "fulfill-order-details-toggle",
	orderDocumentsMenu: "fulfill-order-documents-menu",
	orderInUnitsAndEachesAddLine: "fulfill-order-add-line",
	orderInUnitsAndEachesQuantity: "fulfill-order-line-quantity",
	orderPrintViewClose: "fulfill-order-print-view-close",
	orderPrintViewClosed: "fulfill-order-print-view-closed",
	orderStatusStepper: "fulfill-order-status-stepper",
	portalCheckoutMethod: "fulfill-portal-checkout-method",
	portalCheckoutMethodName: "fulfill-portal-order-contact-name",
	portalCheckoutMethodPhone: "fulfill-portal-order-contact-phone",
	portalItemVisibilityGrade: "fulfill-availability-rules-grade",
	portalItemVisibilityRules: "fulfill-availability-rules",
	portalPlaceOrderAddToCart: "fulfill-portal-preview-add-to-cart",
	portalPlaceOrderCard: "fulfill-portal-preview-card",
	portalRetailPriceEditToggle: "portal-catalog-retail-edit",
	portalRetailPriceNext: "portal-catalog-retail-next",
	portalRetailPricePanel: "portal-catalog-retail-panel",
	portalRetailPricePanelField: "portal-catalog-retail-price-field",
	portalRetailPriceReview: "portal-catalog-retail-review",
	portalRetailPriceSave: "portal-catalog-retail-save",
	portalSettingsBuyerRetailPriceEditing: "fulfill-portal-buyer-retail-price-editing",
	portalSettingsEnabled: "fulfill-portal-enabled",
	portalSettingsSelfRegistration: "fulfill-portal-self-registration",
	quoteFromOpenOrderDates: "fulfill-order-quote-dates",
	quoteFromOpenOrderFollowUpNote: "fulfill-order-quote-follow-up-note",
	quoteFromOpenOrderViewQuote: "fulfill-order-view-quote",
	reasonHintCell: "fulfill-inventory-reason-cell",
	reasonHintEditor: "fulfill-inventory-reason-editor",
	reasonHintUse: "fulfill-inventory-reason-use",
	recordPaymentDetails: "fulfill-receivables-details",
	recordPaymentRecord: "fulfill-receivables-record",
	tourTheNavModules: "nav-module-switcher",
	tourTheNavRibbon: "nav-ribbon",
	tourTheNavSections: "nav-rail-sections",
	unitPricesExplanation: "fulfill-order-per-each-price",
	unitPricesOverride: "fulfill-order-override-unit-price",
	unitPricesPrice: "fulfill-order-unit-price",
	unitPricesSetting: "fulfill-settings-price-per",
	unitPricesWhy: "fulfill-order-why-price"
} as const;

export const WALKTHROUGH_SIGNALS = {
	masterOrderScheduled: "masterOrderScheduled",
	reuseCustomerTypeSaved: "reuse-customer-type-saved",
	reuseCustomerTypeCreated: "reuse-customer-type-created",
	importCustomerMatchesFilled: "import-customer-matches-filled",
	linkItemFromOrderProductionSaved: "linkItemFromOrderProductionSaved",
	linkItemFromOrderSaved: "linkItemFromOrderSaved",
	adjustHardgoodsSaved: "adjustHardgoodsSaved",
	adjustInventorySaved: "adjustInventorySaved",
	buildLoadListSaved: "buildLoadListSaved",
	buildReportCreated: "buildReportCreated",
	createCustomerSaved: "createCustomerSaved",
	createItemSaved: "createItemSaved",
	createKitSaved: "createKitSaved",
	createOrderSaved: "createOrderSaved",
	createPriceLevelSaved: "createPriceLevelSaved",
	createProductionJobSaved: "createProductionJobSaved",
	createPurchaseOrderSaved: "createPurchaseOrderSaved",
	createRecipeSaved: "createRecipeSaved",
	createSiteLocationSaved: "createSiteLocationSaved",
	createVendorSaved: "createVendorSaved",
	editRecipeVariationSaved: "editRecipeVariationSaved",
	importMaterialItemsDone: "importMaterialItemsDone",
	invoiceCreated: "invoiceCreated",
	linkProductionItemSaved: "linkProductionItemSaved",
	massUpdateItemsApplied: "massUpdateItemsApplied",
	orderAcknowledged: "orderAcknowledged",
	orderAllocated: "orderAllocated",
	takeFromOtherOrdersSaved: "takeFromOtherOrdersSaved",
	orderStatusAdvanced: "orderStatusAdvanced",
	paymentRecorded: "paymentRecorded",
	reasonHintUsed: "reasonHintUsed",
	recordProductionLocationsSaved: "recordProductionLocationsSaved",
	unitPricesOverrideSaved: "unitPricesOverrideSaved",
	unitPricesSettingSaved: "unitPricesSettingSaved"
} as const;

const scheduleMasterOrder: WalkthroughDefinition = {
	completionMessageKey: "walkthroughs.scheduleMasterOrder.completionMessage",
	id: "scheduleMasterOrder",
	isAvailable: (state) => state.masterOrder?.masterOrderStateData?.status === MasterOrderStatus.Acknowledged,
	product: ApplicationEnum.FULFILL,
	routePath: "/fulfill/orders/master-order/:masterOrderID",
	titleKey: "walkthroughs.scheduleMasterOrder.title",
	steps: [
		{
			anchorId: TOUR_ANCHORS.scheduleMasterOpen,
			bodyKey: "walkthroughs.scheduleMasterOrder.steps.0.body",
			gate: { effectAnchorId: TOUR_ANCHORS.scheduleMasterStart, kind: "clickTarget" },
			id: "open-schedule",
			placement: "bottom-start",
			titleKey: "walkthroughs.scheduleMasterOrder.steps.0.title"
		},
		{
			anchorId: TOUR_ANCHORS.scheduleMasterStart,
			bodyKey: "walkthroughs.scheduleMasterOrder.steps.1.body",
			gate: { anchorId: TOUR_ANCHORS.scheduleMasterWeek, kind: "elementAppears" },
			id: "choose-weeks",
			placement: "bottom-start",
			titleKey: "walkthroughs.scheduleMasterOrder.steps.1.title"
		},
		{
			anchorId: TOUR_ANCHORS.scheduleMasterWeek,
			bodyKey: "walkthroughs.scheduleMasterOrder.steps.2.body",
			gate: { anchorId: TOUR_ANCHORS.scheduleMasterQuantityEntered, kind: "elementAppears" },
			id: "enter-quantity",
			placement: "bottom-start",
			titleKey: "walkthroughs.scheduleMasterOrder.steps.2.title"
		},
		{
			anchorId: TOUR_ANCHORS.scheduleMasterSave,
			bodyKey: "walkthroughs.scheduleMasterOrder.steps.3.body",
			gate: { kind: "mutationSuccess", signalId: WALKTHROUGH_SIGNALS.masterOrderScheduled },
			id: "schedule-orders",
			placement: "bottom-start",
			titleKey: "walkthroughs.scheduleMasterOrder.steps.3.title"
		},
		{
			anchorId: TOUR_ANCHORS.scheduleMasterDetails,
			bodyKey: "walkthroughs.scheduleMasterOrder.steps.4.body",
			gate: { kind: "manualNext" },
			id: "find-weekly-orders",
			placement: "bottom-start",
			titleKey: "walkthroughs.scheduleMasterOrder.steps.4.title"
		}
	]
};

const adjustHardgoods: WalkthroughDefinition = {
	completionMessageKey: "walkthroughs.adjustHardgoods.completionMessage",
	id: "adjustHardgoods",
	product: ApplicationEnum.PRODUCE,
	routePath: "/produce/inventory/hardgoods",
	titleKey: "walkthroughs.adjustHardgoods.title",
	steps: [
		{
			anchorId: TOUR_ANCHORS.adjustHardgoodsList,
			bodyKey: "walkthroughs.adjustHardgoods.steps.0.body",
			gate: { anchorId: TOUR_ANCHORS.adjustHardgoodsAdjust, kind: "elementAppears" },
			id: "select-item",
			placement: "bottom-start",
			titleKey: "walkthroughs.adjustHardgoods.steps.0.title"
		},
		{
			anchorId: TOUR_ANCHORS.adjustHardgoodsAdjust,
			bodyKey: "walkthroughs.adjustHardgoods.steps.1.body",
			gate: { effectAnchorId: TOUR_ANCHORS.adjustHardgoodsQuantity, kind: "clickTarget" },
			id: "open-adjust",
			placement: "left",
			titleKey: "walkthroughs.adjustHardgoods.steps.1.title"
		},
		{
			anchorId: TOUR_ANCHORS.adjustHardgoodsQuantity,
			bodyKey: "walkthroughs.adjustHardgoods.steps.2.body",
			gate: { anchorId: TOUR_ANCHORS.adjustHardgoodsQuantityEntered, kind: "elementAppears" },
			id: "enter-quantity",
			optionalNoteKey: "walkthroughs.adjustHardgoods.steps.2.optionalNote",
			placement: "bottom-start",
			titleKey: "walkthroughs.adjustHardgoods.steps.2.title"
		},
		{
			anchorId: TOUR_ANCHORS.adjustHardgoodsSave,
			bodyKey: "walkthroughs.adjustHardgoods.steps.3.body",
			gate: { kind: "mutationSuccess", signalId: WALKTHROUGH_SIGNALS.adjustHardgoodsSaved },
			id: "save-adjustment",
			placement: "top",
			titleKey: "walkthroughs.adjustHardgoods.steps.3.title"
		}
	]
};

const adjustInventory: WalkthroughDefinition = {
	completionMessageKey: "walkthroughs.adjustInventory.completionMessage",
	id: "adjustInventory",
	product: ApplicationEnum.FULFILL,
	routePath: "/fulfill/inventory",
	titleKey: "walkthroughs.adjustInventory.title",
	steps: [
		{
			anchorId: TOUR_ANCHORS.adjustInventoryList,
			bodyKey: "walkthroughs.adjustInventory.steps.0.body",
			gate: { anchorId: TOUR_ANCHORS.adjustInventoryAdjust, kind: "elementAppears" },
			id: "select-item",
			placement: "bottom-start",
			titleKey: "walkthroughs.adjustInventory.steps.0.title"
		},
		{
			anchorId: TOUR_ANCHORS.adjustInventoryAdjust,
			bodyKey: "walkthroughs.adjustInventory.steps.1.body",
			gate: { effectAnchorId: TOUR_ANCHORS.adjustInventoryQuantity, kind: "clickTarget" },
			id: "open-adjust",
			placement: "left",
			titleKey: "walkthroughs.adjustInventory.steps.1.title"
		},
		{
			anchorId: TOUR_ANCHORS.adjustInventoryQuantity,
			bodyKey: "walkthroughs.adjustInventory.steps.2.body",
			gate: { anchorId: TOUR_ANCHORS.adjustInventoryQuantityEntered, kind: "elementAppears" },
			id: "enter-quantity",
			optionalNoteKey: "walkthroughs.adjustInventory.steps.2.optionalNote",
			placement: "bottom-start",
			titleKey: "walkthroughs.adjustInventory.steps.2.title"
		},
		{
			anchorId: TOUR_ANCHORS.adjustInventorySave,
			bodyKey: "walkthroughs.adjustInventory.steps.3.body",
			gate: { kind: "mutationSuccess", signalId: WALKTHROUGH_SIGNALS.adjustInventorySaved },
			id: "save-adjustment",
			placement: "top",
			titleKey: "walkthroughs.adjustInventory.steps.3.title"
		}
	]
};

const buildLoadList: WalkthroughDefinition = {
	completionMessageKey: "walkthroughs.buildLoadList.completionMessage",
	id: "buildLoadList",
	product: ApplicationEnum.RESTOCK,
	routePath: "/restock/load_list",
	titleKey: "walkthroughs.buildLoadList.title",
	steps: [
		{
			anchorId: TOUR_ANCHORS.buildLoadListSessionManager,
			bodyKey: "walkthroughs.buildLoadList.steps.0.body",
			gate: { effectAnchorId: TOUR_ANCHORS.buildLoadListNewSession, kind: "clickTarget" },
			id: "open-session-manager",
			placement: "bottom-start",
			titleKey: "walkthroughs.buildLoadList.steps.0.title"
		},
		{
			anchorId: TOUR_ANCHORS.buildLoadListNewSession,
			bodyKey: "walkthroughs.buildLoadList.steps.1.body",
			gate: { effectAnchorId: TOUR_ANCHORS.buildLoadListStations, kind: "clickTarget" },
			id: "create-session",
			optionalNoteKey: "walkthroughs.buildLoadList.steps.1.optionalNote",
			placement: "bottom",
			titleKey: "walkthroughs.buildLoadList.steps.1.title"
		},
		{
			anchorId: TOUR_ANCHORS.buildLoadListStations,
			bodyKey: "walkthroughs.buildLoadList.steps.2.body",
			gate: { effectAnchorId: TOUR_ANCHORS.buildLoadListStationSelect, kind: "clickTarget" },
			id: "choose-stations",
			placement: "right",
			titleKey: "walkthroughs.buildLoadList.steps.2.title"
		},
		{
			anchorId: TOUR_ANCHORS.buildLoadListStationSelect,
			bodyKey: "walkthroughs.buildLoadList.steps.3.body",
			gate: { kind: "manualNext" },
			id: "select-stations",
			optionalNoteKey: "walkthroughs.buildLoadList.steps.3.optionalNote",
			placement: "top",
			titleKey: "walkthroughs.buildLoadList.steps.3.title"
		},
		{
			anchorId: TOUR_ANCHORS.buildLoadListGenerate,
			bodyKey: "walkthroughs.buildLoadList.steps.4.body",
			gate: { kind: "mutationSuccess", signalId: WALKTHROUGH_SIGNALS.buildLoadListSaved },
			id: "generate-load-list",
			placement: "top",
			titleKey: "walkthroughs.buildLoadList.steps.4.title"
		}
	]
};

const createOrder: WalkthroughDefinition = {
	completionMessageKey: "walkthroughs.createOrder.completionMessage",
	id: "createOrder",
	product: ApplicationEnum.FULFILL,
	routePath: "/fulfill/orders",
	titleKey: "walkthroughs.createOrder.title",
	steps: [
		{
			anchorId: TOUR_ANCHORS.createOrderAddOrder,
			bodyKey: "walkthroughs.createOrder.steps.0.body",
			gate: { effectAnchorId: TOUR_ANCHORS.createOrderNewFormOpen, kind: "clickTarget" },
			id: "add-order",
			placement: "bottom-start",
			titleKey: "walkthroughs.createOrder.steps.0.title"
		},
		{
			anchorId: TOUR_ANCHORS.createOrderCustomerPicker,
			bodyKey: "walkthroughs.createOrder.steps.1.body",
			gate: { anchorId: TOUR_ANCHORS.createOrderCustomerSelected, kind: "elementAppears" },
			id: "pick-customer",
			placement: "left",
			titleKey: "walkthroughs.createOrder.steps.1.title"
		},
		{
			anchorId: TOUR_ANCHORS.createOrderDetails,
			bodyKey: "walkthroughs.createOrder.steps.2.body",
			gate: { kind: "manualNext" },
			id: "review-details",
			optionalNoteKey: "walkthroughs.createOrder.steps.2.optionalNote",
			placement: "left",
			titleKey: "walkthroughs.createOrder.steps.2.title"
		},
		{
			anchorId: TOUR_ANCHORS.createOrderSave,
			bodyKey: "walkthroughs.createOrder.steps.3.body",
			gate: { kind: "mutationSuccess", signalId: WALKTHROUGH_SIGNALS.createOrderSaved },
			id: "save-order",
			placement: "top",
			titleKey: "walkthroughs.createOrder.steps.3.title"
		}
	]
};

const createCustomer: WalkthroughDefinition = {
	completionMessageKey: "walkthroughs.createCustomer.completionMessage",
	id: "createCustomer",
	product: ApplicationEnum.FULFILL,
	routePath: "/fulfill/customers",
	titleKey: "walkthroughs.createCustomer.title",
	steps: [
		{
			anchorId: TOUR_ANCHORS.createCustomerAddCustomer,
			bodyKey: "walkthroughs.createCustomer.steps.0.body",
			gate: { effectAnchorId: TOUR_ANCHORS.createCustomerName, kind: "clickTarget" },
			id: "add-customer",
			placement: "bottom-start",
			titleKey: "walkthroughs.createCustomer.steps.0.title"
		},
		{
			anchorId: TOUR_ANCHORS.createCustomerName,
			bodyKey: "walkthroughs.createCustomer.steps.1.body",
			gate: { anchorId: TOUR_ANCHORS.createCustomerNameEntered, kind: "elementAppears" },
			id: "name-customer",
			optionalNoteKey: "walkthroughs.createCustomer.steps.1.optionalNote",
			placement: "bottom-start",
			titleKey: "walkthroughs.createCustomer.steps.1.title"
		},
		{
			anchorId: TOUR_ANCHORS.createCustomerSave,
			bodyKey: "walkthroughs.createCustomer.steps.2.body",
			gate: { kind: "mutationSuccess", signalId: WALKTHROUGH_SIGNALS.createCustomerSaved },
			id: "save-customer",
			placement: "top",
			titleKey: "walkthroughs.createCustomer.steps.2.title"
		}
	]
};

const createItem: WalkthroughDefinition = {
	completionMessageKey: "walkthroughs.createItem.completionMessage",
	id: "createItem",
	product: ApplicationEnum.FULFILL,
	routePath: "/fulfill/product/items",
	titleKey: "walkthroughs.createItem.title",
	steps: [
		{
			anchorId: TOUR_ANCHORS.createItemAddItem,
			bodyKey: "walkthroughs.createItem.steps.0.body",
			gate: { effectAnchorId: TOUR_ANCHORS.createItemName, kind: "clickTarget" },
			id: "add-item",
			placement: "bottom-start",
			titleKey: "walkthroughs.createItem.steps.0.title"
		},
		{
			anchorId: TOUR_ANCHORS.createItemName,
			bodyKey: "walkthroughs.createItem.steps.1.body",
			gate: { anchorId: TOUR_ANCHORS.createItemNameEntered, kind: "elementAppears" },
			id: "name-item",
			placement: "bottom-start",
			titleKey: "walkthroughs.createItem.steps.1.title"
		},
		{
			anchorId: TOUR_ANCHORS.createItemCode,
			bodyKey: "walkthroughs.createItem.steps.2.body",
			gate: { anchorId: TOUR_ANCHORS.createItemCodeEntered, kind: "elementAppears" },
			id: "code-item",
			optionalNoteKey: "walkthroughs.createItem.steps.2.optionalNote",
			placement: "bottom-start",
			titleKey: "walkthroughs.createItem.steps.2.title"
		},
		{
			anchorId: TOUR_ANCHORS.createItemSave,
			bodyKey: "walkthroughs.createItem.steps.3.body",
			gate: { kind: "mutationSuccess", signalId: WALKTHROUGH_SIGNALS.createItemSaved },
			id: "save-item",
			placement: "top",
			titleKey: "walkthroughs.createItem.steps.3.title"
		}
	]
};

const createPurchaseOrder: WalkthroughDefinition = {
	completionMessageKey: "walkthroughs.createPurchaseOrder.completionMessage",
	id: "createPurchaseOrder",
	product: ApplicationEnum.FULFILL,
	routePath: "/fulfill/purchasing/orders",
	spansRoutesUnder: "/fulfill/purchasing/orders",
	titleKey: "walkthroughs.createPurchaseOrder.title",
	steps: [
		{
			anchorId: TOUR_ANCHORS.createPurchaseOrderAddPo,
			bodyKey: "walkthroughs.createPurchaseOrder.steps.0.body",
			gate: { effectAnchorId: TOUR_ANCHORS.createPurchaseOrderNewFormOpen, kind: "clickTarget" },
			id: "add-po",
			placement: "bottom-start",
			titleKey: "walkthroughs.createPurchaseOrder.steps.0.title"
		},
		{
			anchorId: TOUR_ANCHORS.createPurchaseOrderVendor,
			bodyKey: "walkthroughs.createPurchaseOrder.steps.1.body",
			gate: { anchorId: TOUR_ANCHORS.createPurchaseOrderVendorSelected, kind: "elementAppears" },
			id: "pick-vendor",
			placement: "left",
			titleKey: "walkthroughs.createPurchaseOrder.steps.1.title"
		},
		{
			anchorId: TOUR_ANCHORS.createPurchaseOrderLine,
			bodyKey: "walkthroughs.createPurchaseOrder.steps.2.body",
			gate: { anchorId: TOUR_ANCHORS.createPurchaseOrderLineSelected, kind: "elementAppears" },
			id: "pick-line",
			placement: "left",
			titleKey: "walkthroughs.createPurchaseOrder.steps.2.title"
		},
		{
			anchorId: TOUR_ANCHORS.createPurchaseOrderSave,
			bodyKey: "walkthroughs.createPurchaseOrder.steps.3.body",
			gate: { kind: "mutationSuccess", signalId: WALKTHROUGH_SIGNALS.createPurchaseOrderSaved },
			id: "save-po",
			optionalNoteKey: "walkthroughs.createPurchaseOrder.steps.3.optionalNote",
			placement: "top",
			titleKey: "walkthroughs.createPurchaseOrder.steps.3.title"
		}
	]
};

const createRecipe: WalkthroughDefinition = {
	completionMessageKey: "walkthroughs.createRecipe.completionMessage",
	id: "createRecipe",
	product: ApplicationEnum.PRODUCE,
	routePath: "/produce/production/recipes",
	spansRoutesUnder: "/produce/production/recipes",
	titleKey: "walkthroughs.createRecipe.title",
	steps: [
		{
			anchorId: TOUR_ANCHORS.createRecipeAddRecipe,
			bodyKey: "walkthroughs.createRecipe.steps.0.body",
			gate: { effectAnchorId: TOUR_ANCHORS.createRecipeCatalog, kind: "clickTarget" },
			id: "add-recipe",
			placement: "bottom-start",
			titleKey: "walkthroughs.createRecipe.steps.0.title"
		},
		{
			anchorId: TOUR_ANCHORS.createRecipeCatalog,
			bodyKey: "walkthroughs.createRecipe.steps.1.body",
			gate: { kind: "manualNext" },
			id: "pick-catalog",
			placement: "bottom-start",
			titleKey: "walkthroughs.createRecipe.steps.1.title"
		},
		{
			anchorId: TOUR_ANCHORS.createRecipeCategory,
			bodyKey: "walkthroughs.createRecipe.steps.2.body",
			gate: { kind: "manualNext" },
			id: "pick-category",
			placement: "bottom-start",
			titleKey: "walkthroughs.createRecipe.steps.2.title"
		},
		{
			anchorId: TOUR_ANCHORS.createRecipeDetails,
			bodyKey: "walkthroughs.createRecipe.steps.3.body",
			gate: { kind: "manualNext" },
			id: "set-details",
			optionalNoteKey: "walkthroughs.createRecipe.steps.3.optionalNote",
			placement: "top",
			titleKey: "walkthroughs.createRecipe.steps.3.title"
		},
		{
			anchorId: TOUR_ANCHORS.createRecipeSave,
			bodyKey: "walkthroughs.createRecipe.steps.4.body",
			gate: { kind: "mutationSuccess", signalId: WALKTHROUGH_SIGNALS.createRecipeSaved },
			id: "save-recipe",
			placement: "top",
			titleKey: "walkthroughs.createRecipe.steps.4.title"
		}
	]
};

const editRecipeVariationsInGrid: WalkthroughDefinition = {
	completionMessageKey: "walkthroughs.editRecipeVariationsInGrid.completionMessage",
	id: "editRecipeVariationsInGrid",
	product: ApplicationEnum.PRODUCE,
	routePath: "/produce/production/recipes",
	spansRoutesUnder: "/produce/production/recipes",
	titleKey: "walkthroughs.editRecipeVariationsInGrid.title",
	steps: [
		{
			anchorId: TOUR_ANCHORS.editRecipeVariationsGrowWeeks,
			bodyKey: "walkthroughs.editRecipeVariationsInGrid.steps.0.body",
			gate: { anchorId: TOUR_ANCHORS.editRecipeVariationsEditing, kind: "elementAppears" },
			id: "edit-cell",
			optionalNoteKey: "walkthroughs.editRecipeVariationsInGrid.steps.0.optionalNote",
			placement: "bottom",
			titleKey: "walkthroughs.editRecipeVariationsInGrid.steps.0.title"
		},
		{
			anchorId: TOUR_ANCHORS.editRecipeVariationsSaveAway,
			bodyKey: "walkthroughs.editRecipeVariationsInGrid.steps.1.body",
			gate: { kind: "mutationSuccess", signalId: WALKTHROUGH_SIGNALS.editRecipeVariationSaved },
			id: "save-edit",
			placement: "bottom",
			titleKey: "walkthroughs.editRecipeVariationsInGrid.steps.1.title"
		},
		{
			anchorId: TOUR_ANCHORS.editRecipeVariationsUpdateJobs,
			bodyKey: "walkthroughs.editRecipeVariationsInGrid.steps.2.body",
			gate: { kind: "manualNext" },
			id: "update-jobs",
			placement: "bottom-start",
			titleKey: "walkthroughs.editRecipeVariationsInGrid.steps.2.title"
		}
	]
};

const createProductionJob: WalkthroughDefinition = {
	completionMessageKey: "walkthroughs.createProductionJob.completionMessage",
	id: "createProductionJob",
	product: ApplicationEnum.PRODUCE,
	routePath: "/produce/production/schedule",
	titleKey: "walkthroughs.createProductionJob.title",
	steps: [
		{
			anchorId: TOUR_ANCHORS.createProductionJobAddJob,
			bodyKey: "walkthroughs.createProductionJob.steps.0.body",
			gate: { effectAnchorId: TOUR_ANCHORS.createProductionJobRecipe, kind: "clickTarget" },
			id: "add-job",
			placement: "bottom-start",
			titleKey: "walkthroughs.createProductionJob.steps.0.title"
		},
		{
			anchorId: TOUR_ANCHORS.createProductionJobRecipe,
			bodyKey: "walkthroughs.createProductionJob.steps.1.body",
			gate: { kind: "manualNext" },
			id: "pick-recipe",
			placement: "bottom-start",
			titleKey: "walkthroughs.createProductionJob.steps.1.title"
		},
		{
			anchorId: TOUR_ANCHORS.createProductionJobWeek,
			bodyKey: "walkthroughs.createProductionJob.steps.2.body",
			gate: { kind: "manualNext" },
			id: "set-week",
			optionalNoteKey: "walkthroughs.createProductionJob.steps.2.optionalNote",
			placement: "top",
			titleKey: "walkthroughs.createProductionJob.steps.2.title"
		},
		{
			anchorId: TOUR_ANCHORS.createProductionJobSave,
			bodyKey: "walkthroughs.createProductionJob.steps.3.body",
			gate: { kind: "mutationSuccess", signalId: WALKTHROUGH_SIGNALS.createProductionJobSaved },
			id: "save-job",
			placement: "top",
			titleKey: "walkthroughs.createProductionJob.steps.3.title"
		}
	]
};

const recordProductionLocations: WalkthroughDefinition = {
	completionMessageKey: "walkthroughs.recordProductionLocations.completionMessage",
	id: "recordProductionLocations",
	product: ApplicationEnum.PRODUCE,
	routePath: "/produce/production/schedule",
	titleKey: "walkthroughs.recordProductionLocations.title",
	steps: [
		{
			anchorId: TOUR_ANCHORS.recordProductionOverrides,
			bodyKey: "walkthroughs.recordProductionLocations.steps.0.body",
			gate: { effectAnchorId: TOUR_ANCHORS.recordProductionMenuItem, kind: "clickTarget" },
			id: "open-overrides",
			placement: "bottom-start",
			titleKey: "walkthroughs.recordProductionLocations.steps.0.title"
		},
		{
			anchorId: TOUR_ANCHORS.recordProductionMenuItem,
			bodyKey: "walkthroughs.recordProductionLocations.steps.1.body",
			gate: { effectAnchorId: TOUR_ANCHORS.recordProductionQuantity, kind: "clickTarget" },
			id: "record-production",
			placement: "right",
			titleKey: "walkthroughs.recordProductionLocations.steps.1.title"
		},
		{
			anchorId: TOUR_ANCHORS.recordProductionQuantity,
			bodyKey: "walkthroughs.recordProductionLocations.steps.2.body",
			gate: { anchorId: TOUR_ANCHORS.recordProductionQuantityEntered, kind: "elementAppears" },
			id: "enter-quantity",
			placement: "right",
			titleKey: "walkthroughs.recordProductionLocations.steps.2.title"
		},
		{
			anchorId: TOUR_ANCHORS.recordProductionAddLocation,
			bodyKey: "walkthroughs.recordProductionLocations.steps.3.body",
			gate: { effectAnchorId: TOUR_ANCHORS.recordProductionLocationRow, kind: "clickTarget" },
			id: "add-location",
			placement: "right",
			titleKey: "walkthroughs.recordProductionLocations.steps.3.title"
		},
		{
			anchorId: TOUR_ANCHORS.recordProductionPlaced,
			bodyKey: "walkthroughs.recordProductionLocations.steps.4.body",
			gate: { anchorId: TOUR_ANCHORS.recordProductionPlacedReady, kind: "elementAppears" },
			id: "split-quantity",
			optionalNoteKey: "walkthroughs.recordProductionLocations.steps.4.optionalNote",
			placement: "right",
			titleKey: "walkthroughs.recordProductionLocations.steps.4.title"
		},
		{
			anchorId: TOUR_ANCHORS.recordProductionSave,
			bodyKey: "walkthroughs.recordProductionLocations.steps.5.body",
			gate: { kind: "mutationSuccess", signalId: WALKTHROUGH_SIGNALS.recordProductionLocationsSaved },
			id: "save-production",
			placement: "top",
			titleKey: "walkthroughs.recordProductionLocations.steps.5.title"
		}
	]
};

const createSiteLocation: WalkthroughDefinition = {
	completionMessageKey: "walkthroughs.createSiteLocation.completionMessage",
	id: "createSiteLocation",
	product: ApplicationEnum.FULFILL,
	routePath: "/fulfill/locations",
	titleKey: "walkthroughs.createSiteLocation.title",
	steps: [
		{
			anchorId: TOUR_ANCHORS.createSiteLocationAddLocation,
			bodyKey: "walkthroughs.createSiteLocation.steps.0.body",
			gate: { effectAnchorId: TOUR_ANCHORS.createSiteLocationName, kind: "clickTarget" },
			id: "add-location",
			placement: "bottom-start",
			titleKey: "walkthroughs.createSiteLocation.steps.0.title"
		},
		{
			anchorId: TOUR_ANCHORS.createSiteLocationName,
			bodyKey: "walkthroughs.createSiteLocation.steps.1.body",
			gate: { anchorId: TOUR_ANCHORS.createSiteLocationNameEntered, kind: "elementAppears" },
			id: "name-location",
			optionalNoteKey: "walkthroughs.createSiteLocation.steps.1.optionalNote",
			placement: "bottom-start",
			titleKey: "walkthroughs.createSiteLocation.steps.1.title"
		},
		{
			anchorId: TOUR_ANCHORS.createSiteLocationSave,
			bodyKey: "walkthroughs.createSiteLocation.steps.2.body",
			gate: { kind: "mutationSuccess", signalId: WALKTHROUGH_SIGNALS.createSiteLocationSaved },
			id: "save-location",
			placement: "top",
			titleKey: "walkthroughs.createSiteLocation.steps.2.title"
		}
	]
};

const createVendor: WalkthroughDefinition = {
	completionMessageKey: "walkthroughs.createVendor.completionMessage",
	id: "createVendor",
	product: ApplicationEnum.FULFILL,
	routePath: "/fulfill/purchasing/vendors",
	titleKey: "walkthroughs.createVendor.title",
	steps: [
		{
			anchorId: TOUR_ANCHORS.createVendorAddVendor,
			bodyKey: "walkthroughs.createVendor.steps.0.body",
			gate: { effectAnchorId: TOUR_ANCHORS.createVendorName, kind: "clickTarget" },
			id: "add-vendor",
			placement: "bottom-start",
			titleKey: "walkthroughs.createVendor.steps.0.title"
		},
		{
			anchorId: TOUR_ANCHORS.createVendorName,
			bodyKey: "walkthroughs.createVendor.steps.1.body",
			gate: { anchorId: TOUR_ANCHORS.createVendorNameEntered, kind: "elementAppears" },
			id: "name-vendor",
			optionalNoteKey: "walkthroughs.createVendor.steps.1.optionalNote",
			placement: "bottom-start",
			titleKey: "walkthroughs.createVendor.steps.1.title"
		},
		{
			anchorId: TOUR_ANCHORS.createVendorSave,
			bodyKey: "walkthroughs.createVendor.steps.2.body",
			gate: { kind: "mutationSuccess", signalId: WALKTHROUGH_SIGNALS.createVendorSaved },
			id: "save-vendor",
			placement: "top",
			titleKey: "walkthroughs.createVendor.steps.2.title"
		}
	]
};

const createPriceLevel: WalkthroughDefinition = {
	completionMessageKey: "walkthroughs.createPriceLevel.completionMessage",
	id: "createPriceLevel",
	product: ApplicationEnum.FULFILL,
	routePath: "/fulfill/sales/pricing/groups/group/:priceGroupID/:tab?",
	titleKey: "walkthroughs.createPriceLevel.title",
	steps: [
		{
			anchorId: TOUR_ANCHORS.createPriceLevelAddLevel,
			bodyKey: "walkthroughs.createPriceLevel.steps.0.body",
			gate: { effectAnchorId: TOUR_ANCHORS.createPriceLevelTarget, kind: "clickTarget" },
			id: "add-level",
			placement: "bottom-start",
			titleKey: "walkthroughs.createPriceLevel.steps.0.title"
		},
		{
			anchorId: TOUR_ANCHORS.createPriceLevelTarget,
			bodyKey: "walkthroughs.createPriceLevel.steps.1.body",
			gate: { kind: "manualNext" },
			id: "choose-target",
			placement: "bottom-start",
			titleKey: "walkthroughs.createPriceLevel.steps.1.title"
		},
		{
			anchorId: TOUR_ANCHORS.createPriceLevelPrice,
			bodyKey: "walkthroughs.createPriceLevel.steps.2.body",
			gate: { kind: "manualNext" },
			id: "enter-price",
			optionalNoteKey: "walkthroughs.createPriceLevel.steps.2.optionalNote",
			placement: "bottom-start",
			titleKey: "walkthroughs.createPriceLevel.steps.2.title"
		},
		{
			anchorId: TOUR_ANCHORS.createPriceLevelStartDate,
			bodyKey: "walkthroughs.createPriceLevel.steps.3.body",
			gate: { kind: "manualNext" },
			id: "set-start-date",
			placement: "top",
			titleKey: "walkthroughs.createPriceLevel.steps.3.title"
		},
		{
			anchorId: TOUR_ANCHORS.createPriceLevelSave,
			bodyKey: "walkthroughs.createPriceLevel.steps.4.body",
			gate: { kind: "mutationSuccess", signalId: WALKTHROUGH_SIGNALS.createPriceLevelSaved },
			id: "save-price-level",
			placement: "top",
			titleKey: "walkthroughs.createPriceLevel.steps.4.title"
		}
	]
};

const createKit: WalkthroughDefinition = {
	completionMessageKey: "walkthroughs.createKit.completionMessage",
	id: "createKit",
	product: ApplicationEnum.FULFILL,
	routePath: "/fulfill/product/kits",
	spansRoutesUnder: "/fulfill/product/kits",
	titleKey: "walkthroughs.createKit.title",
	steps: [
		{
			anchorId: TOUR_ANCHORS.createKitAddKit,
			bodyKey: "walkthroughs.createKit.steps.0.body",
			gate: { effectAnchorId: TOUR_ANCHORS.createKitWizardOpen, kind: "clickTarget" },
			id: "add-kit",
			placement: "bottom-start",
			titleKey: "walkthroughs.createKit.steps.0.title"
		},
		{
			anchorId: TOUR_ANCHORS.createKitName,
			bodyKey: "walkthroughs.createKit.steps.1.body",
			gate: { anchorId: TOUR_ANCHORS.createKitNameEntered, kind: "elementAppears" },
			id: "name-kit",
			placement: "bottom-start",
			titleKey: "walkthroughs.createKit.steps.1.title"
		},
		{
			anchorId: TOUR_ANCHORS.createKitContainer,
			bodyKey: "walkthroughs.createKit.steps.2.body",
			gate: { anchorId: TOUR_ANCHORS.createKitContainerSelected, kind: "elementAppears" },
			id: "choose-container",
			optionalNoteKey: "walkthroughs.createKit.steps.2.optionalNote",
			placement: "bottom-start",
			titleKey: "walkthroughs.createKit.steps.2.title"
		},
		{
			anchorId: TOUR_ANCHORS.createKitBasicsNext,
			bodyKey: "walkthroughs.createKit.steps.3.body",
			gate: { anchorId: TOUR_ANCHORS.createKitContents, kind: "elementAppears" },
			id: "next-to-contents",
			placement: "top",
			titleKey: "walkthroughs.createKit.steps.3.title"
		},
		{
			anchorId: TOUR_ANCHORS.createKitContents,
			bodyKey: "walkthroughs.createKit.steps.4.body",
			gate: { anchorId: TOUR_ANCHORS.createKitLineAdded, kind: "elementAppears" },
			id: "add-contents",
			placement: "bottom-start",
			titleKey: "walkthroughs.createKit.steps.4.title"
		},
		{
			anchorId: TOUR_ANCHORS.createKitContentsNext,
			bodyKey: "walkthroughs.createKit.steps.5.body",
			gate: { anchorId: TOUR_ANCHORS.createKitReview, kind: "elementAppears" },
			id: "next-to-review",
			placement: "top",
			titleKey: "walkthroughs.createKit.steps.5.title"
		},
		{
			anchorId: TOUR_ANCHORS.createKitReview,
			bodyKey: "walkthroughs.createKit.steps.6.body",
			gate: { kind: "manualNext" },
			id: "review-kit",
			placement: "bottom-start",
			titleKey: "walkthroughs.createKit.steps.6.title"
		},
		{
			anchorId: TOUR_ANCHORS.createKitSave,
			bodyKey: "walkthroughs.createKit.steps.7.body",
			gate: { kind: "mutationSuccess", signalId: WALKTHROUGH_SIGNALS.createKitSaved },
			id: "save-kit",
			placement: "top",
			titleKey: "walkthroughs.createKit.steps.7.title"
		}
	]
};

const acknowledgeOrder: WalkthroughDefinition = {
	completionMessageKey: "walkthroughs.acknowledgeOrder.completionMessage",
	id: "acknowledgeOrder",
	isAvailable: (state) => state.order.orderStateData?.status === OrderStatus.Submitted,
	product: ApplicationEnum.FULFILL,
	routePath: "/fulfill/orders/order/:orderID",
	titleKey: "walkthroughs.acknowledgeOrder.title",
	steps: [
		{
			anchorId: TOUR_ANCHORS.orderStatusStepper,
			bodyKey: "walkthroughs.acknowledgeOrder.steps.0.body",
			gate: { kind: "manualNext" },
			id: "review-order",
			placement: "bottom",
			titleKey: "walkthroughs.acknowledgeOrder.steps.0.title"
		},
		{
			anchorId: TOUR_ANCHORS.orderAdvanceStatus,
			bodyKey: "walkthroughs.acknowledgeOrder.steps.1.body",
			gate: { kind: "mutationSuccess", signalId: WALKTHROUGH_SIGNALS.orderAcknowledged },
			id: "acknowledge-order",
			optionalNoteKey: "walkthroughs.acknowledgeOrder.steps.1.optionalNote",
			placement: "bottom-start",
			titleKey: "walkthroughs.acknowledgeOrder.steps.1.title"
		}
	]
};

const allocateAndPrintPullSlip: WalkthroughDefinition = {
	completionMessageKey: "walkthroughs.allocateAndPrintPullSlip.completionMessage",
	id: "allocateAndPrintPullSlip",
	product: ApplicationEnum.FULFILL,
	routePath: "/fulfill/orders/order/:orderID",
	titleKey: "walkthroughs.allocateAndPrintPullSlip.title",
	steps: [
		{
			anchorId: TOUR_ANCHORS.allocateAndPrintPullSlipAllocate,
			bodyKey: "walkthroughs.allocateAndPrintPullSlip.steps.0.body",
			gate: { effectAnchorId: TOUR_ANCHORS.allocateAndPrintPullSlipQuickAllocate, kind: "clickTarget" },
			id: "open-allocate",
			placement: "bottom-start",
			titleKey: "walkthroughs.allocateAndPrintPullSlip.steps.0.title"
		},
		{
			anchorId: TOUR_ANCHORS.allocateAndPrintPullSlipQuickAllocate,
			bodyKey: "walkthroughs.allocateAndPrintPullSlip.steps.1.body",
			gate: { kind: "mutationSuccess", signalId: WALKTHROUGH_SIGNALS.orderAllocated },
			id: "quick-allocate",
			optionalNoteKey: "walkthroughs.allocateAndPrintPullSlip.steps.1.optionalNote",
			placement: "right",
			titleKey: "walkthroughs.allocateAndPrintPullSlip.steps.1.title"
		},
		{
			anchorId: TOUR_ANCHORS.orderDocumentsMenu,
			bodyKey: "walkthroughs.allocateAndPrintPullSlip.steps.2.body",
			gate: { effectAnchorId: TOUR_ANCHORS.allocateAndPrintPullSlipPickSlip, kind: "clickTarget" },
			id: "open-documents",
			placement: "bottom-start",
			titleKey: "walkthroughs.allocateAndPrintPullSlip.steps.2.title"
		},
		{
			anchorId: TOUR_ANCHORS.allocateAndPrintPullSlipPickSlip,
			bodyKey: "walkthroughs.allocateAndPrintPullSlip.steps.3.body",
			gate: { effectAnchorId: TOUR_ANCHORS.allocateAndPrintPullSlipPickSlipView, kind: "clickTarget" },
			id: "view-pick-slip",
			placement: "right",
			titleKey: "walkthroughs.allocateAndPrintPullSlip.steps.3.title"
		}
	]
};

const takeFromOtherOrders: WalkthroughDefinition = {
	completionMessageKey: "walkthroughs.takeFromOtherOrders.completionMessage",
	id: "takeFromOtherOrders",
	isAvailable: (state) => isAllocationEditable(state.order.orderStateData?.status),
	product: ApplicationEnum.FULFILL,
	routePath: "/fulfill/orders/order/:orderID",
	titleKey: "walkthroughs.takeFromOtherOrders.title",
	steps: [
		{
			anchorId: TOUR_ANCHORS.allocateAndPrintPullSlipAllocate,
			bodyKey: "walkthroughs.takeFromOtherOrders.steps.0.body",
			gate: { effectAnchorId: TOUR_ANCHORS.takeFromOtherOrdersAdvanced, kind: "clickTarget" },
			id: "open-allocate",
			placement: "bottom-start",
			titleKey: "walkthroughs.takeFromOtherOrders.steps.0.title"
		},
		{
			anchorId: TOUR_ANCHORS.takeFromOtherOrdersAdvanced,
			bodyKey: "walkthroughs.takeFromOtherOrders.steps.1.body",
			gate: { effectAnchorId: TOUR_ANCHORS.takeFromOtherOrdersDialogOpen, kind: "clickTarget" },
			id: "allocate-advanced",
			placement: "right",
			titleKey: "walkthroughs.takeFromOtherOrders.steps.1.title"
		},
		{
			anchorId: TOUR_ANCHORS.takeFromOtherOrdersLine,
			bodyKey: "walkthroughs.takeFromOtherOrders.steps.2.body",
			gate: { anchorId: TOUR_ANCHORS.takeFromOtherOrdersClaimsLink, kind: "elementAppears" },
			id: "expand-line",
			placement: "bottom",
			titleKey: "walkthroughs.takeFromOtherOrders.steps.2.title"
		},
		{
			anchorId: TOUR_ANCHORS.takeFromOtherOrdersClaimsLink,
			bodyKey: "walkthroughs.takeFromOtherOrders.steps.3.body",
			gate: { effectAnchorId: TOUR_ANCHORS.takeFromOtherOrdersClaimsPanel, kind: "clickTarget" },
			id: "show-claims",
			placement: "bottom",
			titleKey: "walkthroughs.takeFromOtherOrders.steps.3.title"
		},
		{
			anchorId: TOUR_ANCHORS.takeFromOtherOrdersTakeInput,
			bodyKey: "walkthroughs.takeFromOtherOrders.steps.4.body",
			gate: { anchorId: TOUR_ANCHORS.takeFromOtherOrdersTakeEntered, kind: "elementAppears" },
			id: "take-units",
			optionalNoteKey: "walkthroughs.takeFromOtherOrders.steps.4.optionalNote",
			placement: "left",
			titleKey: "walkthroughs.takeFromOtherOrders.steps.4.title"
		},
		{
			anchorId: TOUR_ANCHORS.takeFromOtherOrdersSave,
			bodyKey: "walkthroughs.takeFromOtherOrders.steps.5.body",
			gate: { effectAnchorId: TOUR_ANCHORS.takeFromOtherOrdersConfirm, kind: "clickTarget" },
			id: "save-take",
			placement: "top",
			titleKey: "walkthroughs.takeFromOtherOrders.steps.5.title"
		},
		{
			anchorId: TOUR_ANCHORS.takeFromOtherOrdersConfirm,
			bodyKey: "walkthroughs.takeFromOtherOrders.steps.6.body",
			gate: { kind: "mutationSuccess", signalId: WALKTHROUGH_SIGNALS.takeFromOtherOrdersSaved },
			id: "confirm-take",
			placement: "top",
			titleKey: "walkthroughs.takeFromOtherOrders.steps.6.title"
		}
	]
};

const printShipDayPaperwork: WalkthroughDefinition = {
	completionMessageKey: "walkthroughs.printShipDayPaperwork.completionMessage",
	id: "printShipDayPaperwork",
	product: ApplicationEnum.FULFILL,
	routePath: "/fulfill/logistics/deliveries",
	titleKey: "walkthroughs.printShipDayPaperwork.title",
	steps: [
		{
			anchorId: TOUR_ANCHORS.printShipDayPaperworkShipDay,
			bodyKey: "walkthroughs.printShipDayPaperwork.steps.0.body",
			gate: { anchorId: TOUR_ANCHORS.printShipDayPaperworkDayPicked, kind: "elementAppears" },
			id: "pick-ship-day",
			placement: "bottom-start",
			titleKey: "walkthroughs.printShipDayPaperwork.steps.0.title"
		},
		{
			anchorId: TOUR_ANCHORS.printShipDayPaperworkSelection,
			bodyKey: "walkthroughs.printShipDayPaperwork.steps.1.body",
			gate: { anchorId: TOUR_ANCHORS.printShipDayPaperworkSelected, kind: "elementAppears" },
			id: "select-deliveries",
			optionalNoteKey: "walkthroughs.printShipDayPaperwork.steps.1.optionalNote",
			placement: "bottom-start",
			titleKey: "walkthroughs.printShipDayPaperwork.steps.1.title"
		},
		{
			anchorId: TOUR_ANCHORS.printShipDayPaperworkPrint,
			bodyKey: "walkthroughs.printShipDayPaperwork.steps.2.body",
			gate: { effectAnchorId: TOUR_ANCHORS.printShipDayPaperworkDialogPrint, kind: "clickTarget" },
			id: "open-paperwork",
			placement: "bottom-start",
			titleKey: "walkthroughs.printShipDayPaperwork.steps.2.title"
		},
		{
			anchorId: TOUR_ANCHORS.printShipDayPaperworkDialogPrint,
			bodyKey: "walkthroughs.printShipDayPaperwork.steps.3.body",
			gate: { anchorId: TOUR_ANCHORS.printShipDayPaperworkViewer, kind: "elementAppears" },
			id: "print-document",
			placement: "left",
			titleKey: "walkthroughs.printShipDayPaperwork.steps.3.title"
		}
	]
};

const buildReport: WalkthroughDefinition = {
	completionMessageKey: "walkthroughs.buildReport.completionMessage",
	id: "buildReport",
	product: ApplicationEnum.REPORTS,
	routePath: "/reports",
	titleKey: "walkthroughs.buildReport.title",
	steps: [
		{
			anchorId: TOUR_ANCHORS.buildReportBlankReport,
			bodyKey: "walkthroughs.buildReport.steps.0.body",
			gate: { effectAnchorId: TOUR_ANCHORS.buildReportName, kind: "clickTarget" },
			id: "blank-report",
			placement: "bottom-start",
			titleKey: "walkthroughs.buildReport.steps.0.title"
		},
		{
			anchorId: TOUR_ANCHORS.buildReportName,
			bodyKey: "walkthroughs.buildReport.steps.1.body",
			gate: { kind: "manualNext" },
			id: "name-report",
			placement: "bottom",
			titleKey: "walkthroughs.buildReport.steps.1.title"
		},
		{
			anchorId: TOUR_ANCHORS.buildReportDataSources,
			bodyKey: "walkthroughs.buildReport.steps.2.body",
			gate: { kind: "manualNext" },
			id: "pick-data-source",
			placement: "top",
			titleKey: "walkthroughs.buildReport.steps.2.title"
		},
		{
			anchorId: TOUR_ANCHORS.buildReportCreate,
			bodyKey: "walkthroughs.buildReport.steps.3.body",
			gate: { kind: "mutationSuccess", signalId: WALKTHROUGH_SIGNALS.buildReportCreated },
			id: "create-report",
			placement: "top",
			titleKey: "walkthroughs.buildReport.steps.3.title"
		}
	]
};

const canCreateInvoice = (state: WalkthroughAvailabilityState): boolean => {
	const order = state.order.orderStateData;
	if (!order || isBpfTenant(state.settings.tenantId)) return false;
	return orderDocumentActions(order.status, order.invoiceStatus, false).invoice.create.available;
};

const createInvoice: WalkthroughDefinition = {
	completionMessageKey: "walkthroughs.createInvoice.completionMessage",
	id: "createInvoice",
	isAvailable: canCreateInvoice,
	product: ApplicationEnum.FULFILL,
	routePath: "/fulfill/orders/order/:orderID",
	titleKey: "walkthroughs.createInvoice.title",
	steps: [
		{
			anchorId: TOUR_ANCHORS.orderDocumentsMenu,
			bodyKey: "walkthroughs.createInvoice.steps.0.body",
			gate: { effectAnchorId: TOUR_ANCHORS.createInvoiceIssue, kind: "clickTarget" },
			id: "open-documents",
			placement: "bottom-start",
			titleKey: "walkthroughs.createInvoice.steps.0.title"
		},
		{
			anchorId: TOUR_ANCHORS.createInvoiceIssue,
			bodyKey: "walkthroughs.createInvoice.steps.1.body",
			gate: { kind: "mutationSuccess", signalId: WALKTHROUGH_SIGNALS.invoiceCreated },
			id: "create-invoice",
			placement: "right",
			titleKey: "walkthroughs.createInvoice.steps.1.title"
		}
	]
};

const filterAndExport: WalkthroughDefinition = {
	completionMessageKey: "walkthroughs.filterAndExport.completionMessage",
	id: "filterAndExport",
	product: ApplicationEnum.FULFILL,
	routePath: "/fulfill/orders",
	titleKey: "walkthroughs.filterAndExport.title",
	steps: [
		{
			anchorId: TOUR_ANCHORS.globalSearch,
			bodyKey: "walkthroughs.filterAndExport.steps.0.body",
			gate: { kind: "manualNext" },
			id: "search-orders",
			optionalNoteKey: "walkthroughs.filterAndExport.steps.0.optionalNote",
			placement: "bottom",
			titleKey: "walkthroughs.filterAndExport.steps.0.title"
		},
		{
			anchorId: TOUR_ANCHORS.filterAndExportExport,
			bodyKey: "walkthroughs.filterAndExport.steps.1.body",
			gate: { kind: "manualNext" },
			id: "export-orders",
			placement: "bottom-start",
			titleKey: "walkthroughs.filterAndExport.steps.1.title"
		},
		{
			anchorId: TOUR_ANCHORS.gridViewTab,
			bodyKey: "walkthroughs.filterAndExport.steps.2.body",
			gate: { effectAnchorId: TOUR_ANCHORS.gridSaveLayout, kind: "clickTarget" },
			id: "view-tab",
			placement: "bottom-start",
			titleKey: "walkthroughs.filterAndExport.steps.2.title"
		},
		{
			anchorId: TOUR_ANCHORS.gridSaveLayout,
			bodyKey: "walkthroughs.filterAndExport.steps.3.body",
			gate: { kind: "manualNext" },
			id: "save-layout",
			placement: "bottom",
			titleKey: "walkthroughs.filterAndExport.steps.3.title"
		}
	]
};

const importCustomerMatches: WalkthroughDefinition = {
	id: "importCustomerMatches",
	product: ApplicationEnum.FULFILL,
	routePath: "/fulfill/customers/import",
	titleKey: "walkthroughs.importCustomerMatches.title",
	completionMessageKey: "walkthroughs.importCustomerMatches.completionMessage",
	steps: [
		{
			id: "upload",
			anchorId: TOUR_ANCHORS.importCustomerMatchesUpload,
			gate: { kind: "elementAppears", anchorId: TOUR_ANCHORS.importCustomerMatchesColumn },
			titleKey: "walkthroughs.importCustomerMatches.steps.0.title",
			bodyKey: "walkthroughs.importCustomerMatches.steps.0.body",
			placement: "bottom"
		},
		{
			id: "use",
			anchorId: TOUR_ANCHORS.importCustomerMatchesColumn,
			gate: { kind: "elementAppears", anchorId: TOUR_ANCHORS.importCustomerMatchesUsed },
			titleKey: "walkthroughs.importCustomerMatches.steps.1.title",
			bodyKey: "walkthroughs.importCustomerMatches.steps.1.body",
			placement: "left"
		},
		{
			id: "same-as",
			anchorId: TOUR_ANCHORS.importCustomerMatchesSameAs,
			gate: { kind: "manualNext" },
			titleKey: "walkthroughs.importCustomerMatches.steps.2.title",
			bodyKey: "walkthroughs.importCustomerMatches.steps.2.body",
			placement: "bottom"
		},
		{
			id: "fill",
			anchorId: TOUR_ANCHORS.importCustomerMatchesRibbon,
			gate: { kind: "clickTarget", effectAnchorId: TOUR_ANCHORS.importCustomerMatchesConfirm },
			titleKey: "walkthroughs.importCustomerMatches.steps.3.title",
			bodyKey: "walkthroughs.importCustomerMatches.steps.3.body",
			placement: "bottom"
		},
		{
			id: "confirm",
			anchorId: TOUR_ANCHORS.importCustomerMatchesConfirm,
			gate: { kind: "mutationSuccess", signalId: WALKTHROUGH_SIGNALS.importCustomerMatchesFilled },
			titleKey: "walkthroughs.importCustomerMatches.steps.4.title",
			bodyKey: "walkthroughs.importCustomerMatches.steps.4.body",
			placement: "top"
		},
		{
			id: "undo",
			anchorId: TOUR_ANCHORS.importCustomerMatchesUndo,
			gate: { kind: "clickTarget", effectAnchorId: TOUR_ANCHORS.importCustomerMatchesUndone },
			titleKey: "walkthroughs.importCustomerMatches.steps.5.title",
			bodyKey: "walkthroughs.importCustomerMatches.steps.5.body",
			placement: "bottom"
		}
	]
};

const importMaterialItems: WalkthroughDefinition = {
	completionMessageKey: "walkthroughs.importMaterialItems.completionMessage",
	id: "importMaterialItems",
	product: ApplicationEnum.PRODUCE,
	routePath: "/produce/production/material-items/import",
	titleKey: "walkthroughs.importMaterialItems.title",
	steps: [
		{
			anchorId: TOUR_ANCHORS.importMaterialItemsTemplate,
			bodyKey: "walkthroughs.importMaterialItems.steps.0.body",
			gate: { kind: "manualNext" },
			id: "download-template",
			placement: "top",
			titleKey: "walkthroughs.importMaterialItems.steps.0.title"
		},
		{
			anchorId: TOUR_ANCHORS.importMaterialItemsUpload,
			bodyKey: "walkthroughs.importMaterialItems.steps.1.body",
			gate: {
				anchorId: TOUR_ANCHORS.importMaterialItemsFileChosen,
				kind: "elementAppears"
			},
			id: "choose-file",
			optionalNoteKey: "walkthroughs.importMaterialItems.steps.1.optionalNote",
			placement: "top",
			titleKey: "walkthroughs.importMaterialItems.steps.1.title"
		},
		{
			anchorId: TOUR_ANCHORS.importMaterialItemsStepper,
			bodyKey: "walkthroughs.importMaterialItems.steps.2.body",
			gate: { kind: "manualNext" },
			id: "check-validation",
			placement: "bottom",
			titleKey: "walkthroughs.importMaterialItems.steps.2.title"
		},
		{
			anchorId: TOUR_ANCHORS.importMaterialItemsContinue,
			bodyKey: "walkthroughs.importMaterialItems.steps.3.body",
			gate: {
				kind: "mutationSuccess",
				signalId: WALKTHROUGH_SIGNALS.importMaterialItemsDone
			},
			id: "run-import",
			placement: "top",
			titleKey: "walkthroughs.importMaterialItems.steps.3.title"
		}
	]
};

const linkProductionItem: WalkthroughDefinition = {
	completionMessageKey: "walkthroughs.linkProductionItem.completionMessage",
	id: "linkProductionItem",
	product: ApplicationEnum.PRODUCE,
	routePath: "/produce/production/items/production-items",
	titleKey: "walkthroughs.linkProductionItem.title",
	steps: [
		{
			anchorId: TOUR_ANCHORS.linkProductionItemList,
			bodyKey: "walkthroughs.linkProductionItem.steps.0.body",
			gate: { anchorId: TOUR_ANCHORS.linkProductionItemSalesItem, kind: "elementAppears" },
			id: "open-item",
			placement: "bottom-start",
			titleKey: "walkthroughs.linkProductionItem.steps.0.title"
		},
		{
			anchorId: TOUR_ANCHORS.linkProductionItemSalesItem,
			bodyKey: "walkthroughs.linkProductionItem.steps.1.body",
			gate: { kind: "manualNext" },
			id: "pick-sales-item",
			placement: "bottom-start",
			titleKey: "walkthroughs.linkProductionItem.steps.1.title"
		},
		{
			anchorId: TOUR_ANCHORS.linkProductionItemSave,
			bodyKey: "walkthroughs.linkProductionItem.steps.2.body",
			gate: { kind: "mutationSuccess", signalId: WALKTHROUGH_SIGNALS.linkProductionItemSaved },
			id: "save-link",
			placement: "top",
			titleKey: "walkthroughs.linkProductionItem.steps.2.title"
		}
	]
};

const linkItemFromOrder: WalkthroughDefinition = {
	completionMessageKey: "walkthroughs.linkItemFromOrder.completionMessage",
	id: "linkItemFromOrder",
	isAvailable: (state) => allowsAddingLineItems(state.order.orderStateData?.status),
	product: ApplicationEnum.FULFILL,
	routePath: "/fulfill/orders/order/:orderID",
	spansRoutesUnder: "/fulfill",
	titleKey: "walkthroughs.linkItemFromOrder.title",
	steps: [
		{
			anchorId: TOUR_ANCHORS.orderInUnitsAndEachesAddLine,
			bodyKey: "walkthroughs.linkItemFromOrder.steps.0.body",
			gate: { kind: "clickTarget", effectAnchorId: TOUR_ANCHORS.linkItemFromOrderPicker },
			id: "add-line",
			placement: "bottom-start",
			titleKey: "walkthroughs.linkItemFromOrder.steps.0.title"
		},
		{
			anchorId: TOUR_ANCHORS.linkItemFromOrderPicker,
			bodyKey: "walkthroughs.linkItemFromOrder.steps.1.body",
			gate: { kind: "elementAppears", anchorId: TOUR_ANCHORS.createItemName },
			id: "new-item",
			placement: "bottom-start",
			titleKey: "walkthroughs.linkItemFromOrder.steps.1.title"
		},
		{
			anchorId: TOUR_ANCHORS.linkItemFromOrderProduction,
			bodyKey: "walkthroughs.linkItemFromOrder.steps.2.body",
			gate: { kind: "elementAppears", anchorId: TOUR_ANCHORS.linkItemFromOrderNumber },
			id: "item-details",
			placement: "left-start",
			titleKey: "walkthroughs.linkItemFromOrder.steps.2.title",
			optionalNoteKey: "walkthroughs.linkItemFromOrder.steps.2.optionalNote"
		},
		{
			anchorId: TOUR_ANCHORS.linkItemFromOrderNumber,
			bodyKey: "walkthroughs.linkItemFromOrder.steps.3.body",
			gate: { kind: "mutationSuccess", signalId: WALKTHROUGH_SIGNALS.linkItemFromOrderProductionSaved },
			id: "save-production",
			placement: "bottom-start",
			titleKey: "walkthroughs.linkItemFromOrder.steps.3.title",
			optionalNoteKey: "walkthroughs.linkItemFromOrder.steps.3.optionalNote"
		},
		{
			anchorId: TOUR_ANCHORS.linkItemFromOrderRecipe,
			bodyKey: "walkthroughs.linkItemFromOrder.steps.4.body",
			gate: { kind: "elementAppears", anchorId: TOUR_ANCHORS.createRecipeSave },
			id: "add-recipe",
			placement: "bottom-start",
			titleKey: "walkthroughs.linkItemFromOrder.steps.4.title"
		},
		{
			anchorId: TOUR_ANCHORS.createRecipeSave,
			bodyKey: "walkthroughs.linkItemFromOrder.steps.5.body",
			gate: { kind: "mutationSuccess", signalId: WALKTHROUGH_SIGNALS.createRecipeSaved },
			id: "save-recipe",
			placement: "bottom-start",
			titleKey: "walkthroughs.linkItemFromOrder.steps.5.title",
			optionalNoteKey: "walkthroughs.linkItemFromOrder.steps.5.optionalNote"
		},
		{
			anchorId: TOUR_ANCHORS.createItemSave,
			bodyKey: "walkthroughs.linkItemFromOrder.steps.6.body",
			gate: { kind: "mutationSuccess", signalId: WALKTHROUGH_SIGNALS.linkItemFromOrderSaved },
			id: "save-item",
			placement: "bottom-start",
			titleKey: "walkthroughs.linkItemFromOrder.steps.6.title"
		}
	]
};

const enterUnitPrices: WalkthroughDefinition = {
	completionMessageKey: "walkthroughs.enterUnitPrices.completionMessage",
	id: "enterUnitPrices",
	product: ApplicationEnum.FULFILL,
	routePath: "/fulfill/settings/general",
	spansRoutesUnder: "/fulfill",
	titleKey: "walkthroughs.enterUnitPrices.title",
	steps: [
		{
			anchorId: TOUR_ANCHORS.unitPricesSetting,
			bodyKey: "walkthroughs.enterUnitPrices.steps.0.body",
			gate: { kind: "mutationSuccess", signalId: WALKTHROUGH_SIGNALS.unitPricesSettingSaved },
			id: "choose-unit",
			placement: "bottom-start",
			titleKey: "walkthroughs.enterUnitPrices.steps.0.title"
		},
		{
			anchorId: TOUR_ANCHORS.unitPricesSetting,
			bodyKey: "walkthroughs.enterUnitPrices.steps.1.body",
			gate: { kind: "elementAppears", anchorId: TOUR_ANCHORS.unitPricesPrice },
			id: "open-unit-order",
			placement: "bottom-start",
			titleKey: "walkthroughs.enterUnitPrices.steps.1.title"
		},
		{
			anchorId: TOUR_ANCHORS.unitPricesPrice,
			bodyKey: "walkthroughs.enterUnitPrices.steps.2.body",
			gate: { kind: "manualNext" },
			id: "unit-price",
			placement: "bottom-start",
			titleKey: "walkthroughs.enterUnitPrices.steps.2.title"
		},
		{
			anchorId: TOUR_ANCHORS.unitPricesOverride,
			bodyKey: "walkthroughs.enterUnitPrices.steps.3.body",
			gate: { kind: "mutationSuccess", signalId: WALKTHROUGH_SIGNALS.unitPricesOverrideSaved },
			id: "override-unit-price",
			placement: "bottom-start",
			titleKey: "walkthroughs.enterUnitPrices.steps.3.title"
		},
		{
			anchorId: TOUR_ANCHORS.unitPricesWhy,
			bodyKey: "walkthroughs.enterUnitPrices.steps.4.body",
			gate: { kind: "clickTarget", effectAnchorId: TOUR_ANCHORS.unitPricesExplanation },
			id: "why-price",
			placement: "bottom-start",
			titleKey: "walkthroughs.enterUnitPrices.steps.4.title"
		}
	]
};

const orderInUnitsAndEaches: WalkthroughDefinition = {
	completionMessageKey: "walkthroughs.orderInUnitsAndEaches.completionMessage",
	id: "orderInUnitsAndEaches",
	isAvailable: (state) => allowsAddingLineItems(state.order.orderStateData?.status),
	product: ApplicationEnum.FULFILL,
	routePath: "/fulfill/orders/order/:orderID",
	titleKey: "walkthroughs.orderInUnitsAndEaches.title",
	steps: [
		{
			anchorId: TOUR_ANCHORS.orderInUnitsAndEachesAddLine,
			bodyKey: "walkthroughs.orderInUnitsAndEaches.steps.0.body",
			gate: { effectAnchorId: TOUR_ANCHORS.orderInUnitsAndEachesQuantity, kind: "clickTarget" },
			id: "add-line",
			placement: "bottom-start",
			titleKey: "walkthroughs.orderInUnitsAndEaches.steps.0.title"
		},
		{
			anchorId: TOUR_ANCHORS.orderInUnitsAndEachesQuantity,
			bodyKey: "walkthroughs.orderInUnitsAndEaches.steps.1.body",
			gate: { kind: "manualNext" },
			id: "enter-quantity",
			optionalNoteKey: "walkthroughs.orderInUnitsAndEaches.steps.1.optionalNote",
			placement: "bottom-start",
			titleKey: "walkthroughs.orderInUnitsAndEaches.steps.1.title"
		}
	]
};

const orderStatusLadder: WalkthroughDefinition = {
	completionMessageKey: "walkthroughs.orderStatusLadder.completionMessage",
	id: "orderStatusLadder",
	product: ApplicationEnum.FULFILL,
	routePath: "/fulfill/orders/order/:orderID",
	titleKey: "walkthroughs.orderStatusLadder.title",
	steps: [
		{
			anchorId: TOUR_ANCHORS.orderStatusStepper,
			bodyKey: "walkthroughs.orderStatusLadder.steps.0.body",
			gate: { kind: "manualNext" },
			id: "status-ladder",
			placement: "bottom",
			titleKey: "walkthroughs.orderStatusLadder.steps.0.title"
		},
		{
			anchorId: TOUR_ANCHORS.orderAdvanceStatus,
			bodyKey: "walkthroughs.orderStatusLadder.steps.1.body",
			gate: {
				kind: "mutationSuccess",
				signalId: [WALKTHROUGH_SIGNALS.orderStatusAdvanced, WALKTHROUGH_SIGNALS.orderAcknowledged]
			},
			id: "advance-status",
			placement: "bottom-start",
			titleKey: "walkthroughs.orderStatusLadder.steps.1.title"
		},
		{
			anchorId: TOUR_ANCHORS.orderAdvanceStatus,
			bodyKey: "walkthroughs.orderStatusLadder.steps.2.body",
			gate: {
				kind: "mutationSuccess",
				signalId: [WALKTHROUGH_SIGNALS.orderStatusAdvanced, WALKTHROUGH_SIGNALS.orderAcknowledged]
			},
			id: "advance-again",
			optionalNoteKey: "walkthroughs.orderStatusLadder.steps.2.optionalNote",
			placement: "bottom-start",
			titleKey: "walkthroughs.orderStatusLadder.steps.2.title"
		}
	]
};

const portalItemVisibility: WalkthroughDefinition = {
	completionMessageKey: "walkthroughs.portalItemVisibility.completionMessage",
	id: "portalItemVisibility",
	product: ApplicationEnum.FULFILL,
	routePath: "/fulfill/inventory/availability",
	titleKey: "walkthroughs.portalItemVisibility.title",
	steps: [
		{
			anchorId: TOUR_ANCHORS.portalItemVisibilityRules,
			bodyKey: "walkthroughs.portalItemVisibility.steps.0.body",
			gate: { kind: "manualNext" },
			id: "review-rules",
			placement: "bottom",
			titleKey: "walkthroughs.portalItemVisibility.steps.0.title"
		},
		{
			anchorId: TOUR_ANCHORS.portalItemVisibilityRules,
			bodyKey: "walkthroughs.portalItemVisibility.steps.1.body",
			gate: { kind: "manualNext" },
			id: "min-grade",
			placement: "bottom",
			titleKey: "walkthroughs.portalItemVisibility.steps.1.title"
		},
		{
			anchorId: TOUR_ANCHORS.portalItemVisibilityRules,
			bodyKey: "walkthroughs.portalItemVisibility.steps.2.body",
			gate: { kind: "manualNext" },
			id: "location",
			placement: "bottom",
			titleKey: "walkthroughs.portalItemVisibility.steps.2.title"
		},
		{
			anchorId: TOUR_ANCHORS.portalItemVisibilityRules,
			bodyKey: "walkthroughs.portalItemVisibility.steps.3.body",
			gate: { kind: "manualNext" },
			id: "hard-goods",
			placement: "bottom",
			titleKey: "walkthroughs.portalItemVisibility.steps.3.title"
		},
		{
			anchorId: TOUR_ANCHORS.portalItemVisibilityRules,
			bodyKey: "walkthroughs.portalItemVisibility.steps.4.body",
			gate: { kind: "manualNext" },
			id: "price-rule",
			optionalNoteKey: "walkthroughs.portalItemVisibility.steps.4.optionalNote",
			placement: "bottom",
			titleKey: "walkthroughs.portalItemVisibility.steps.4.title"
		},
		{
			anchorId: TOUR_ANCHORS.portalItemVisibilityRules,
			bodyKey: "walkthroughs.portalItemVisibility.steps.5.body",
			gate: { anchorId: TOUR_ANCHORS.portalItemVisibilityGrade, kind: "elementAppears" },
			id: "edit-rules",
			placement: "bottom",
			titleKey: "walkthroughs.portalItemVisibility.steps.5.title"
		}
	]
};

const portalPlaceOrder: WalkthroughDefinition = {
	completionMessageKey: "walkthroughs.portalPlaceOrder.completionMessage",
	id: "portalPlaceOrder",
	product: ApplicationEnum.FULFILL,
	routePath: "/fulfill/portal/branding",
	titleKey: "walkthroughs.portalPlaceOrder.title",
	steps: [
		{
			anchorId: TOUR_ANCHORS.portalPlaceOrderCard,
			bodyKey: "walkthroughs.portalPlaceOrder.steps.0.body",
			gate: { kind: "manualNext" },
			id: "buyer-view",
			optionalNoteKey: "walkthroughs.portalPlaceOrder.steps.0.optionalNote",
			placement: "top",
			titleKey: "walkthroughs.portalPlaceOrder.steps.0.title"
		},
		{
			anchorId: TOUR_ANCHORS.portalPlaceOrderAddToCart,
			bodyKey: "walkthroughs.portalPlaceOrder.steps.1.body",
			gate: { kind: "manualNext" },
			id: "add-to-cart",
			optionalNoteKey: "walkthroughs.portalPlaceOrder.steps.1.optionalNote",
			placement: "top",
			titleKey: "walkthroughs.portalPlaceOrder.steps.1.title"
		},
		{
			anchorId: TOUR_ANCHORS.portalPlaceOrderCard,
			bodyKey: "walkthroughs.portalPlaceOrder.steps.2.body",
			gate: { kind: "manualNext" },
			id: "order-lands",
			placement: "top",
			titleKey: "walkthroughs.portalPlaceOrder.steps.2.title"
		}
	]
};

const portalCheckoutMethod: WalkthroughDefinition = {
	completionMessageKey: "walkthroughs.portalCheckoutMethod.completionMessage",
	id: "portalCheckoutMethod",
	product: ApplicationEnum.FULFILL,
	routePath: "/fulfill/portal/settings",
	titleKey: "walkthroughs.portalCheckoutMethod.title",
	steps: [
		{
			anchorId: TOUR_ANCHORS.portalCheckoutMethod,
			bodyKey: "walkthroughs.portalCheckoutMethod.steps.0.body",
			gate: { kind: "manualNext" },
			id: "checkout-method",
			placement: "bottom",
			titleKey: "walkthroughs.portalCheckoutMethod.steps.0.title"
		},
		{
			anchorId: TOUR_ANCHORS.portalCheckoutMethodName,
			bodyKey: "walkthroughs.portalCheckoutMethod.steps.1.body",
			gate: { kind: "manualNext" },
			id: "order-contact-name",
			optionalNoteKey: "walkthroughs.portalCheckoutMethod.steps.1.optionalNote",
			placement: "bottom",
			titleKey: "walkthroughs.portalCheckoutMethod.steps.1.title"
		},
		{
			anchorId: TOUR_ANCHORS.portalCheckoutMethodPhone,
			bodyKey: "walkthroughs.portalCheckoutMethod.steps.2.body",
			gate: { kind: "manualNext" },
			id: "order-contact-phone",
			placement: "bottom",
			titleKey: "walkthroughs.portalCheckoutMethod.steps.2.title"
		}
	]
};

const portalSettings: WalkthroughDefinition = {
	completionMessageKey: "walkthroughs.portalSettings.completionMessage",
	id: "portalSettings",
	product: ApplicationEnum.FULFILL,
	routePath: "/fulfill/portal/settings",
	titleKey: "walkthroughs.portalSettings.title",
	steps: [
		{
			anchorId: TOUR_ANCHORS.portalSettingsEnabled,
			bodyKey: "walkthroughs.portalSettings.steps.0.body",
			gate: { kind: "manualNext" },
			id: "enable-portal",
			placement: "bottom",
			titleKey: "walkthroughs.portalSettings.steps.0.title"
		},
		{
			anchorId: TOUR_ANCHORS.portalSettingsSelfRegistration,
			bodyKey: "walkthroughs.portalSettings.steps.1.body",
			gate: { kind: "manualNext" },
			id: "self-registration",
			placement: "bottom",
			titleKey: "walkthroughs.portalSettings.steps.1.title"
		}
	]
};

const canSendQuote = (state: WalkthroughAvailabilityState): boolean => {
	const order = state.order.orderStateData;
	return order?.status === OrderStatus.Open && order.type !== OrderType.Credit && !isBpfTenant(state.settings.tenantId);
};

const quoteFromOpenOrder: WalkthroughDefinition = {
	completionMessageKey: "walkthroughs.quoteFromOpenOrder.completionMessage",
	id: "quoteFromOpenOrder",
	isAvailable: canSendQuote,
	product: ApplicationEnum.FULFILL,
	routePath: "/fulfill/orders/order/:orderID",
	titleKey: "walkthroughs.quoteFromOpenOrder.title",
	steps: [
		{
			anchorId: TOUR_ANCHORS.orderStatusStepper,
			bodyKey: "walkthroughs.quoteFromOpenOrder.steps.0.body",
			gate: { kind: "manualNext" },
			id: "start-open-order",
			placement: "bottom",
			titleKey: "walkthroughs.quoteFromOpenOrder.steps.0.title"
		},
		{
			anchorId: TOUR_ANCHORS.orderDocumentsMenu,
			bodyKey: "walkthroughs.quoteFromOpenOrder.steps.1.body",
			gate: { effectAnchorId: TOUR_ANCHORS.quoteFromOpenOrderViewQuote, kind: "clickTarget" },
			id: "open-documents",
			placement: "bottom-start",
			titleKey: "walkthroughs.quoteFromOpenOrder.steps.1.title"
		},
		{
			anchorId: TOUR_ANCHORS.quoteFromOpenOrderViewQuote,
			bodyKey: "walkthroughs.quoteFromOpenOrder.steps.2.body",
			gate: { effectAnchorId: TOUR_ANCHORS.orderPrintViewClose, kind: "clickTarget" },
			id: "view-quote",
			optionalNoteKey: "walkthroughs.quoteFromOpenOrder.steps.2.optionalNote",
			placement: "right",
			titleKey: "walkthroughs.quoteFromOpenOrder.steps.2.title"
		},
		{
			anchorId: TOUR_ANCHORS.orderPrintViewClose,
			bodyKey: "walkthroughs.quoteFromOpenOrder.steps.3.body",
			gate: { effectAnchorId: TOUR_ANCHORS.orderPrintViewClosed, kind: "clickTarget" },
			id: "read-quote",
			placement: "bottom-end",
			titleKey: "walkthroughs.quoteFromOpenOrder.steps.3.title"
		},
		{
			anchorId: TOUR_ANCHORS.orderDetailsToggle,
			bodyKey: "walkthroughs.quoteFromOpenOrder.steps.4.body",
			gate: { anchorId: TOUR_ANCHORS.quoteFromOpenOrderDates, kind: "elementAppears" },
			id: "open-order-details",
			placement: "bottom-end",
			titleKey: "walkthroughs.quoteFromOpenOrder.steps.4.title"
		},
		{
			anchorId: TOUR_ANCHORS.quoteFromOpenOrderDates,
			bodyKey: "walkthroughs.quoteFromOpenOrder.steps.5.body",
			gate: { kind: "manualNext" },
			id: "quote-dates",
			optionalNoteKey: "walkthroughs.quoteFromOpenOrder.steps.5.optionalNote",
			placement: "left",
			titleKey: "walkthroughs.quoteFromOpenOrder.steps.5.title"
		},
		{
			anchorId: TOUR_ANCHORS.quoteFromOpenOrderFollowUpNote,
			bodyKey: "walkthroughs.quoteFromOpenOrder.steps.6.body",
			gate: { kind: "manualNext" },
			id: "follow-up-note",
			optionalNoteKey: "walkthroughs.quoteFromOpenOrder.steps.6.optionalNote",
			placement: "left",
			titleKey: "walkthroughs.quoteFromOpenOrder.steps.6.title"
		},
		{
			anchorId: TOUR_ANCHORS.orderAdvanceStatus,
			bodyKey: "walkthroughs.quoteFromOpenOrder.steps.7.body",
			gate: { kind: "manualNext" },
			id: "submit-when-agreed",
			placement: "bottom-start",
			titleKey: "walkthroughs.quoteFromOpenOrder.steps.7.title"
		}
	]
};

// apiEndpoints pulls in Vite's import.meta.env, which Playwright cannot load
const fulfilledCachedData = <T>(state: WalkthroughAvailabilityState, endpointName: string): T | undefined =>
	Object.values(state.api.queries).find((query) => query?.endpointName === endpointName && query.status === "fulfilled")
		?.data as T | undefined;

const canTourReasonFromTypedWhy = (state: WalkthroughAvailabilityState): boolean =>
	fulfilledCachedData<GetFernieSuggestionsSettingQuery>(state, "GetFernieSuggestionsSetting")?.tenant
		?.fernieSuggestionsEnabled === true &&
	fulfilledCachedData<GetAllFeatureFlagsQuery>(state, "GetAllFeatureFlags")?.INLINE_INVENTORY_EDIT?.enabled === true;

const reasonFromTypedWhy: WalkthroughDefinition = {
	completionMessageKey: "walkthroughs.reasonFromTypedWhy.completionMessage",
	id: "reasonFromTypedWhy",
	isAvailable: canTourReasonFromTypedWhy,
	product: ApplicationEnum.FULFILL,
	routePath: "/fulfill/inventory",
	titleKey: "walkthroughs.reasonFromTypedWhy.title",
	steps: [
		{
			anchorId: TOUR_ANCHORS.reasonHintCell,
			bodyKey: "walkthroughs.reasonFromTypedWhy.steps.0.body",
			gate: { kind: "manualNext" },
			id: "see-reason",
			placement: "top",
			titleKey: "walkthroughs.reasonFromTypedWhy.steps.0.title"
		},
		{
			anchorId: TOUR_ANCHORS.reasonHintCell,
			bodyKey: "walkthroughs.reasonFromTypedWhy.steps.1.body",
			gate: { anchorId: TOUR_ANCHORS.reasonHintEditor, kind: "elementAppears" },
			id: "type-why",
			optionalNoteKey: "walkthroughs.reasonFromTypedWhy.steps.1.optionalNote",
			placement: "top",
			titleKey: "walkthroughs.reasonFromTypedWhy.steps.1.title"
		},
		{
			anchorId: TOUR_ANCHORS.reasonHintUse,
			bodyKey: "walkthroughs.reasonFromTypedWhy.steps.2.body",
			gate: { kind: "mutationSuccess", signalId: WALKTHROUGH_SIGNALS.reasonHintUsed },
			id: "use-code",
			placement: "bottom-start",
			titleKey: "walkthroughs.reasonFromTypedWhy.steps.2.title"
		}
	]
};

const recordPayment: WalkthroughDefinition = {
	completionMessageKey: "walkthroughs.recordPayment.completionMessage",
	id: "recordPayment",
	product: ApplicationEnum.FULFILL,
	routePath: "/fulfill/sales/receivables",
	titleKey: "walkthroughs.recordPayment.title",
	steps: [
		{
			anchorId: TOUR_ANCHORS.globalSearch,
			bodyKey: "walkthroughs.recordPayment.steps.0.body",
			gate: { anchorId: TOUR_ANCHORS.recordPaymentDetails, kind: "elementAppears" },
			id: "select-invoice",
			placement: "bottom",
			titleKey: "walkthroughs.recordPayment.steps.0.title"
		},
		{
			anchorId: TOUR_ANCHORS.recordPaymentDetails,
			bodyKey: "walkthroughs.recordPayment.steps.1.body",
			gate: { kind: "manualNext" },
			id: "enter-payment",
			placement: "top",
			titleKey: "walkthroughs.recordPayment.steps.1.title"
		},
		{
			anchorId: TOUR_ANCHORS.recordPaymentRecord,
			bodyKey: "walkthroughs.recordPayment.steps.2.body",
			gate: { kind: "mutationSuccess", signalId: WALKTHROUGH_SIGNALS.paymentRecorded },
			id: "record-payment",
			placement: "top",
			titleKey: "walkthroughs.recordPayment.steps.2.title"
		}
	]
};

const tourTheNav: WalkthroughDefinition = {
	completionMessageKey: "walkthroughs.tourTheNav.completionMessage",
	id: "tourTheNav",
	product: ApplicationEnum.FULFILL,
	routePath: "/fulfill/orders",
	titleKey: "walkthroughs.tourTheNav.title",
	steps: [
		{
			anchorId: TOUR_ANCHORS.tourTheNavModules,
			bodyKey: "walkthroughs.tourTheNav.steps.0.body",
			gate: { kind: "manualNext" },
			id: "module-switcher",
			placement: "right",
			titleKey: "walkthroughs.tourTheNav.steps.0.title"
		},
		{
			anchorId: TOUR_ANCHORS.tourTheNavSections,
			bodyKey: "walkthroughs.tourTheNav.steps.1.body",
			gate: { kind: "manualNext" },
			id: "rail-sections",
			placement: "right",
			titleKey: "walkthroughs.tourTheNav.steps.1.title"
		},
		{
			anchorId: TOUR_ANCHORS.tourTheNavRibbon,
			bodyKey: "walkthroughs.tourTheNav.steps.2.body",
			gate: { kind: "manualNext" },
			id: "ribbon",
			placement: "bottom-start",
			titleKey: "walkthroughs.tourTheNav.steps.2.title"
		},
		{
			anchorId: TOUR_ANCHORS.globalSearch,
			bodyKey: "walkthroughs.tourTheNav.steps.3.body",
			gate: { kind: "manualNext" },
			id: "global-search",
			placement: "bottom",
			titleKey: "walkthroughs.tourTheNav.steps.3.title"
		}
	]
};

const massUpdateItems: WalkthroughDefinition = {
	completionMessageKey: "walkthroughs.massUpdateItems.completionMessage",
	id: "massUpdateItems",
	product: ApplicationEnum.FULFILL,
	routePath: "/fulfill/product/items",
	titleKey: "walkthroughs.massUpdateItems.title",
	steps: [
		{
			anchorId: TOUR_ANCHORS.massUpdateItemsOpen,
			bodyKey: "walkthroughs.massUpdateItems.steps.0.body",
			gate: { effectAnchorId: TOUR_ANCHORS.massUpdateItemsSearch, kind: "clickTarget" },
			id: "open-mass-update",
			placement: "bottom-start",
			titleKey: "walkthroughs.massUpdateItems.steps.0.title"
		},
		{
			anchorId: TOUR_ANCHORS.massUpdateItemsSearch,
			bodyKey: "walkthroughs.massUpdateItems.steps.1.body",
			gate: { anchorId: TOUR_ANCHORS.massUpdateItemsChosen, kind: "elementAppears" },
			id: "choose-items",
			optionalNoteKey: "walkthroughs.massUpdateItems.steps.1.optionalNote",
			placement: "right-start",
			titleKey: "walkthroughs.massUpdateItems.steps.1.title"
		},
		{
			anchorId: TOUR_ANCHORS.massUpdateItemsNext,
			bodyKey: "walkthroughs.massUpdateItems.steps.2.body",
			gate: { effectAnchorId: TOUR_ANCHORS.massUpdateItemsField, kind: "clickTarget" },
			id: "continue-to-changes",
			placement: "top",
			titleKey: "walkthroughs.massUpdateItems.steps.2.title"
		},
		{
			anchorId: TOUR_ANCHORS.massUpdateItemsField,
			bodyKey: "walkthroughs.massUpdateItems.steps.3.body",
			gate: { anchorId: TOUR_ANCHORS.massUpdateItemsChangeReady, kind: "elementAppears" },
			id: "choose-change",
			placement: "bottom-start",
			titleKey: "walkthroughs.massUpdateItems.steps.3.title"
		},
		{
			anchorId: TOUR_ANCHORS.massUpdateItemsNext,
			bodyKey: "walkthroughs.massUpdateItems.steps.4.body",
			gate: { effectAnchorId: TOUR_ANCHORS.massUpdateItemsReview, kind: "clickTarget" },
			id: "continue-to-review",
			placement: "top",
			titleKey: "walkthroughs.massUpdateItems.steps.4.title"
		},
		{
			anchorId: TOUR_ANCHORS.massUpdateItemsApply,
			bodyKey: "walkthroughs.massUpdateItems.steps.5.body",
			gate: { kind: "mutationSuccess", signalId: WALKTHROUGH_SIGNALS.massUpdateItemsApplied },
			id: "apply-changes",
			placement: "top",
			titleKey: "walkthroughs.massUpdateItems.steps.5.title"
		},
		{
			anchorId: TOUR_ANCHORS.massUpdateItemsResults,
			bodyKey: "walkthroughs.massUpdateItems.steps.6.body",
			gate: { kind: "manualNext" },
			id: "read-results",
			placement: "bottom-start",
			titleKey: "walkthroughs.massUpdateItems.steps.6.title"
		}
	]
};

const allocateToProduction: WalkthroughDefinition = {
	id: "allocateToProduction",
	product: ApplicationEnum.FULFILL,
	routePath: "/fulfill/orders",
	titleKey: "walkthroughs.allocateToProduction.title",
	completionMessageKey: "walkthroughs.allocateToProduction.completionMessage",
	isAvailable: (state) => {
		const tenant = fulfilledCachedData<GetTenantQuery>(state, "GetTenant")?.tenant;
		const me = fulfilledCachedData<GetMeQuery>(state, "GetMe")?.me;
		return productionAllocationAccess(
			tenant?.produceEnabled ?? false,
			undefined,
			me?.roles,
			tenant?.producePlanFromOrderAccess
		);
	},
	steps: [
		{
			id: "select-orders",
			anchorId: TOUR_ANCHORS.allocateToProductionOpen,
			bodyKey: "walkthroughs.allocateToProduction.steps.0.body",
			titleKey: "walkthroughs.allocateToProduction.steps.0.title",
			placement: "bottom",
			gate: { kind: "manualNext" }
		},
		{
			id: "open",
			anchorId: TOUR_ANCHORS.allocateToProductionOpen,
			bodyKey: "walkthroughs.allocateToProduction.steps.1.body",
			titleKey: "walkthroughs.allocateToProduction.steps.1.title",
			placement: "bottom",
			gate: { kind: "clickTarget", effectAnchorId: TOUR_ANCHORS.allocateToProductionSettings }
		},
		{
			id: "settings",
			anchorId: TOUR_ANCHORS.allocateToProductionSettings,
			bodyKey: "walkthroughs.allocateToProduction.steps.2.body",
			titleKey: "walkthroughs.allocateToProduction.steps.2.title",
			placement: "bottom",
			gate: { kind: "elementAppears", anchorId: TOUR_ANCHORS.allocateToProductionPlanSelected }
		},
		{
			id: "preview",
			anchorId: TOUR_ANCHORS.allocateToProductionPreview,
			bodyKey: "walkthroughs.allocateToProduction.steps.3.body",
			titleKey: "walkthroughs.allocateToProduction.steps.3.title",
			placement: "top",
			gate: { kind: "clickTarget", effectAnchorId: TOUR_ANCHORS.allocateToProductionResults }
		},
		{
			id: "review",
			anchorId: TOUR_ANCHORS.allocateToProductionResults,
			bodyKey: "walkthroughs.allocateToProduction.steps.4.body",
			titleKey: "walkthroughs.allocateToProduction.steps.4.title",
			placement: "top",
			gate: { kind: "manualNext" }
		}
	]
};

const reuseCustomerType: WalkthroughDefinition = {
	completionMessageKey: "walkthroughs.reuseCustomerType.completionMessage",
	id: "reuseCustomerType",
	isAvailable: (state) =>
		fulfilledCachedData<GetFernieSuggestionsSettingQuery>(state, "GetFernieSuggestionsSetting")?.tenant
			?.fernieSuggestionsEnabled === true,
	product: ApplicationEnum.FULFILL,
	routePath: "/fulfill/customers/customer/:customerID",
	titleKey: "walkthroughs.reuseCustomerType.title",
	steps: [
		{
			id: "type-close-name",
			placement: "top-start",
			anchorId: TOUR_ANCHORS.reuseCustomerTypeField,
			gate: { kind: "elementAppears", anchorId: TOUR_ANCHORS.reuseCustomerTypeSuggestion },
			titleKey: "walkthroughs.reuseCustomerType.steps.0.title",
			bodyKey: "walkthroughs.reuseCustomerType.steps.0.body",
			optionalNoteKey: "walkthroughs.reuseCustomerType.steps.0.optionalNote"
		},
		{
			id: "use-existing-type",
			placement: "bottom-start",
			anchorId: TOUR_ANCHORS.reuseCustomerTypeUse,
			gate: { kind: "clickTarget", effectAnchorId: TOUR_ANCHORS.reuseCustomerTypeUsed },
			titleKey: "walkthroughs.reuseCustomerType.steps.1.title",
			bodyKey: "walkthroughs.reuseCustomerType.steps.1.body"
		},
		{
			id: "add-typed-name",
			placement: "top-start",
			anchorId: TOUR_ANCHORS.reuseCustomerTypeField,
			gate: { kind: "mutationSuccess", signalId: WALKTHROUGH_SIGNALS.reuseCustomerTypeCreated },
			titleKey: "walkthroughs.reuseCustomerType.steps.2.title",
			bodyKey: "walkthroughs.reuseCustomerType.steps.2.body"
		},
		{
			id: "save-typed-name",
			placement: "top",
			anchorId: TOUR_ANCHORS.reuseCustomerTypeSave,
			gate: { kind: "mutationSuccess", signalId: WALKTHROUGH_SIGNALS.reuseCustomerTypeSaved },
			titleKey: "walkthroughs.reuseCustomerType.steps.3.title",
			bodyKey: "walkthroughs.reuseCustomerType.steps.3.body"
		}
	]
};

const WALKTHROUGHS: Record<string, WalkthroughDefinition> = {
	[scheduleMasterOrder.id]: scheduleMasterOrder,
	[reuseCustomerType.id]: reuseCustomerType,
	[allocateToProduction.id]: allocateToProduction,
	[acknowledgeOrder.id]: acknowledgeOrder,
	[adjustHardgoods.id]: adjustHardgoods,
	[adjustInventory.id]: adjustInventory,
	[allocateAndPrintPullSlip.id]: allocateAndPrintPullSlip,
	[takeFromOtherOrders.id]: takeFromOtherOrders,
	[buildLoadList.id]: buildLoadList,
	[buildReport.id]: buildReport,
	[createCustomer.id]: createCustomer,
	[createInvoice.id]: createInvoice,
	[createItem.id]: createItem,
	[linkItemFromOrder.id]: linkItemFromOrder,
	[createKit.id]: createKit,
	[createOrder.id]: createOrder,
	[createPriceLevel.id]: createPriceLevel,
	[createProductionJob.id]: createProductionJob,
	[createPurchaseOrder.id]: createPurchaseOrder,
	[createRecipe.id]: createRecipe,
	[createSiteLocation.id]: createSiteLocation,
	[createVendor.id]: createVendor,
	[editRecipeVariationsInGrid.id]: editRecipeVariationsInGrid,
	[enterUnitPrices.id]: enterUnitPrices,
	[filterAndExport.id]: filterAndExport,
	[importMaterialItems.id]: importMaterialItems,
	[importCustomerMatches.id]: importCustomerMatches,
	[linkProductionItem.id]: linkProductionItem,
	[massUpdateItems.id]: massUpdateItems,
	[orderInUnitsAndEaches.id]: orderInUnitsAndEaches,
	[orderStatusLadder.id]: orderStatusLadder,
	[portalCheckoutMethod.id]: portalCheckoutMethod,
	[portalItemVisibility.id]: portalItemVisibility,
	[portalPlaceOrder.id]: portalPlaceOrder,
	[portalSettings.id]: portalSettings,
	[printShipDayPaperwork.id]: printShipDayPaperwork,
	[quoteFromOpenOrder.id]: quoteFromOpenOrder,
	[reasonFromTypedWhy.id]: reasonFromTypedWhy,
	[recordPayment.id]: recordPayment,
	[recordProductionLocations.id]: recordProductionLocations,
	[tourTheNav.id]: tourTheNav
};

export const getWalkthrough = (id: string): WalkthroughDefinition | undefined => WALKTHROUGHS[id];

export const selectIsAwaitingWalkthroughSignal = ({ walkthrough }: RootState, signalId: string): boolean => {
	if (walkthrough.status !== "running" || !walkthrough.activeTourId) return false;
	const gate = getWalkthrough(walkthrough.activeTourId)?.steps[walkthrough.stepIndex]?.gate;
	return gate?.kind === "mutationSuccess" && [gate.signalId].flat().includes(signalId);
};

export const listWalkthroughs = (): WalkthroughDefinition[] => Object.values(WALKTHROUGHS);

// end:true keeps static routePaths exact like the old `===`, while param/optional segments (e.g. ":tab?") still resolve.
export const matchesWalkthroughRoute = (pathname: string, routePath: string): boolean =>
	matchPath({ end: true, path: routePath }, pathname) !== null;

export const staysOnWalkthroughRoute = (pathname: string, definition: WalkthroughDefinition): boolean =>
	definition.spansRoutesUnder !== undefined &&
	matchPath({ end: false, path: definition.spansRoutesUnder }, pathname) !== null;

export const findWalkthroughsForRoute = (
	pathname: string,
	state: WalkthroughAvailabilityState
): WalkthroughDefinition[] =>
	Object.values(WALKTHROUGHS).filter(
		(definition) => matchesWalkthroughRoute(pathname, definition.routePath) && (definition.isAvailable?.(state) ?? true)
	);
